import { Component, ElementRef, HostListener, Input, OnInit, OnDestroy, ViewChild, QueryList, ViewChildren } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { MenuItem, MessageService, SortEvent } from 'primeng/api';
import { Menu } from 'primeng/menu';
import { Subscription, forkJoin, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ProdutosDTO } from '../../../models/produto.models';
import { GeneroItem } from '../../../models/item.model';
import { ProdutosService } from '../../../services/produtos/produto.service';
import { ApiService } from '../../../services/api.service';
import { EmpresaConfigService } from '../../../services/configuracoes/empresa.service';
import { MoedaDTO } from '../../../models/configuracoes/empresa.model';
import { UnidadeMedidaDeItemService } from '../../../services/configuracoes/unidade-de-medida.service';
import { GrupoService } from '../../../services/grupo/grupo.service';
import { SubGrupoService } from '../../../services/grupo/sub-grupo.service';
import { MarcaService } from '../../../services/marca/marca.service';
import { PessoaService } from '../../../services/pessoas/pessoas.service';
import { ImpressoraService } from '../../../services/impressora/impressora.service';
import { ProdutoVarianteModificadorService } from '../../../services/produto-variante-modificador/produto-variante-modificador.service';
import { TableColumn } from '../../../models/shared/table-column.model';
import { TipoDeImposto } from '../../../enums/tipoDeImposto.enum';
import { TipoDeDesconto } from '../../../enums/TipoDeDesconto.enum';
import { TipoItem } from '../../../enums/TipoItem.enum';
import {
  VarianteDePrecoDeItemDTO,
  GrupoDeModificadorDePrecoDeItemDTO,
  ModificadorDePrecoDeItemDTO,
} from '../../../models/produto-variante-modificador/produto-variante-modificador.model';
import { animate, style, transition, trigger } from '@angular/animations';
import { AjusteEstoqueService } from '../../../services/estoque/ajuste-estoque.service';
import { AjusteDeEstoqueDTO, TipoOperacaoDeEstoque, TipoContaEstoque } from '../../../models/estoque/ajuste-estoque.model';
import { slugificar, slugificarCategoria } from '../../cardapio-digital/utils/cardapio-slug.util';
import { toLocalISOString, fromLocalISOString } from '../../../shared/utils/date-utils';
import { validarPromocaoNovidade } from '../../../shared/utils/promocao-novidade.util';
import { extrairErros400 } from '../../../shared/utils/error-utils';
import { ImagemUtils } from '../../../shared/utils/imagem-utils';

@Component({
  selector: 'app-lista-de-produtos',
  templateUrl: './lista-de-produtos.component.html',
  styleUrls: ['./lista-de-produtos.component.scss'],
  providers: [MessageService],
  animations: [
    trigger('fadeAnimation', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('200ms ease-in', style({ opacity: 1 })),
      ]),
      transition(':leave', [
        animate('200ms ease-out', style({ opacity: 0 })),
      ]),
    ]),
  ],
})
export class ListaDeProdutosComponent implements OnInit, OnDestroy {
  env = environment;

  /**
   * Modo EMBUTIDO (aba "Gestão de itens" do Operar Cliente). Quando true, o
   * componente opera sob o CONTEXTO do cliente (X-Empresa-Contexto): esconde o
   * cabeçalho da página, ignora os query params da rota (que são do Operar
   * Cliente) e oculta as ações que sairiam do contexto/fora do escopo add-edit
   * (excluir, ajustar estoque, extrato/ajuste de estoque, importar planilha,
   * voltar para /produtos).
   */
  @Input() embutido = false;

  /**
   * Id da empresa do CLIENTE (só no modo embutido). Usado para montar a URL das
   * imagens dos itens — no contexto, `sessionStorage.id` é a empresa do CONTADOR,
   * não a do cliente.
   */
  @Input() empresaContextoId?: number | null;

  /**
   * Moeda padrao da empresa do CLIENTE (só no modo embutido), já resolvida pelo
   * componente PAI sob o contexto do contador (GET /configuracaoEmpresa/contador).
   *
   * Existe porque `GET /configuracaoEmpresa/sessao` — a fonte usada no modo
   * normal — NAO honra o contexto do cliente: e uma rota global (menu/header),
   * fica deliberadamente fora do allowlist do `ContadorContextoInterceptor` e
   * ainda e cacheada por sessao de usuario (`shareReplay`). No contexto do
   * contador ela responde pela empresa do CONTADOR, o que fazia o formulario
   * mascarar preco/custo na moeda errada (prefixo "Gs" e 0 casas decimais num
   * cliente em Dolar, gravando valores 100x maiores).
   *
   * Mesmo padrao ja adotado no `CotacaoDialogComponent` (a moeda chega por
   * @Input do pai, que a resolveu no contexto correto). SEM este input o
   * comportamento historico via sessao e mantido, entao `/produtos` fica intacta.
   */
  @Input() moedaCliente?: MoedaDTO | null;

  // =========================================================================
  // Controle de abas (0 = Cadastro, 1 = Lista)
  // =========================================================================
  activeTabIndex: number = 1;

  // =========================================================================
  // ViewChild / ViewChildren
  // =========================================================================
  @ViewChild('searchInput') searchInputRef!: ElementRef<HTMLInputElement>;
  @ViewChild('menuAcoes') menuAcoes!: Menu;
  menuItems: MenuItem[] = [];

  @ViewChildren('varianteDescricaoInput') varianteDescricaoInputs!: QueryList<ElementRef<HTMLInputElement>>;
  @ViewChildren('grupoDescricaoInput') grupoDescricaoInputs!: QueryList<ElementRef<HTMLInputElement>>;
  @ViewChildren('modificadorDescricaoInput') modificadorDescricaoInputs!: QueryList<ElementRef<HTMLInputElement>>;

  // =========================================================================
  // Estado da Lista (tabela)
  // =========================================================================
  produtos: ProdutosDTO[] = [];
  produtoSelecionado: ProdutosDTO | null = null;
  first: number = 0;
  rows: number = 10;
  private _lastRows: number = 10;
  totalRecords: number = 0;
  loading: boolean = true;
  searchNome: string = '';
  lastfield: string = 'id';
  lastSort: string = 'asc';

  pageSizeOptions = [
    { label: 5, value: 5 },
    { label: 10, value: 10 },
    { label: 20, value: 20 },
    { label: 120, value: 120 },
  ];

  // Filtro por fornecedor (via queryParam)
  fornecedorId: number | null = null;
  fornecedorNome: string = '';

  // empresa/usuario
  idEmpresa: string = '';

  // Slug da loja no Mini Shop (urlDoMiniShopping da empresa logada). Usado para
  // montar a URL publica do item (/menu/{slugLoja}?categoria=...&item=...) na
  // acao de compartilhar. Carregado em carregarConfiguracaoEmpresa().
  slugLojaMiniShop: string = '';

  // Dialogs da lista
  displayDialogImage: boolean = false;
  displayDialogSaldo: boolean = false;
  deleteDialogVisible: boolean = false;
  displayDialogEtiqueta: boolean = false;
  saldoOriginal: number = 0;
  produtoEtiqueta: ProdutosDTO | null = null;
  deleteItemId: number | null = null;
  expandedImageUrl: string = '';

  // Dialog de visualizacao de imagens secundarias (Tab Lista)
  produtoSelecionadoParaImagens: ProdutosDTO | null = null;

  // Rastreia imagens que falharam ao carregar (por id do produto)
  imagensComErro: { [key: number]: boolean } = {};

  // Moeda padrao da empresa (carregada via EmpresaConfigService.getSessao()).
  // Defaults pre-configurados para Real (BRL) — sao sobrescritos quando a
  // resposta da API chega. Esses campos sao usados tanto na exibicao da
  // tabela quanto nos inputs monetarios do formulario (preco/custo/variante/
  // modificador), garantindo prefixo (R$, $, Gs) e formato consistentes com
  // a moeda da empresa logada.
  moedaPadrao: 'BRL' | 'PYG' | 'USD' | 'ARS' | string = 'BRL';
  localeMoeda: string = 'pt-BR';
  digitsMoeda: string = '1.2-2';
  digitsMoedaPYG: string = '1.0-0';

  // Configuracao de colunas visiveis
  readonly STORAGE_KEY = 'lista-de-produtos-visible-columns';

  columns: TableColumn[] = [
    { key: 'imagem', labelKey: 'produtos.labels.imagem', visible: true, fixed: false, defaultVisible: true },
    { key: 'codigoItem', labelKey: 'produtos.labels.codigo_item', visible: true, fixed: true, defaultVisible: true },
    // Codigo de barras: opcional e DESLIGADA por padrao — quem nao usa leitor nao
    // ganha uma coluna a mais, e quem usa liga uma vez (a escolha fica salva).
    { key: 'barras', labelKey: 'produtos.labels.codigo_de_barras', visible: false, fixed: false, defaultVisible: false },
    { key: 'nome', labelKey: 'produtos.labels.nome', visible: true, fixed: true, defaultVisible: true },
    { key: 'grupo', labelKey: 'produtos.labels.grupo', visible: true, fixed: false, defaultVisible: true },
    { key: 'subGrupo', labelKey: 'produtos.labels.sub_grupo', visible: true, fixed: false, defaultVisible: true },
    { key: 'marca', labelKey: 'produtos.labels.marca', visible: true, fixed: false, defaultVisible: true },
    { key: 'saldo', labelKey: 'produtos.labels.saldo', visible: true, fixed: false, defaultVisible: true },
    { key: 'custo', labelKey: 'produtos.labels.preco_de_custo', visible: true, fixed: false, defaultVisible: true },
    { key: 'precoDeVenda', labelKey: 'produtos.labels.preco_de_venda', visible: true, fixed: false, defaultVisible: true },
    { key: 'acoes', labelKey: 'produtos.labels.acoes', visible: true, fixed: true, defaultVisible: true },
  ];

  // =========================================================================
  // Estado do Formulario (Cadastro/Edicao)
  // =========================================================================
  form: ProdutosDTO = this.createEmptyForm();
  isFormSubmitted = false;

  // ---- Promocao e Novidade (estado dos p-calendar fora do form: ProdutosDTO)
  promocao = false;
  promocaoAteEmData: Date | null = null;
  novidade = false;
  novidadeAteEmData: Date | null = null;

  // ---- Campos exibidos no shop: indicado (texto) e genero (dropdown)
  indicado = '';
  genero: GeneroItem | null = null;
  /**
   * Opcoes do dropdown de genero. A lista de valores e buscada do backend
   * (GET /enums/generoItem) em buildGeneroOpcoes(); o array abaixo e apenas
   * fallback caso a requisicao falhe. O label e a chave i18n derivada do valor.
   */
  generoOpcoes: { label: string; value: GeneroItem }[] = [
    { label: 'produtos.genero.masculino', value: 'MASCULINO' },
    { label: 'produtos.genero.feminino', value: 'FEMININO' },
    { label: 'produtos.genero.unissex', value: 'UNISSEX' },
    { label: 'produtos.genero.prefiro_nao_informar', value: 'PREFIRO_NAO_INFORMAR' },
  ];
  /** Data minima dos calendarios — impede selecao no passado (reforco de RN02/RN04). */
  dataMinimaPromocaoNovidade: Date = new Date();
  /** `true` quando o item em edicao inline e de sistema — oculta a secao (RN08). */
  ehItemDeSistema = false;

  /** Retorna true quando o tipo do item selecionado e SERVICO. */
  get ehServico(): boolean {
    return this.form?.tipoItem?.value === TipoItem.SERVICO;
  }

  // Strings formatadas para exibicao nos inputs monetarios (mascara).
  // Valor inicial reflete precoDeVenda = 0 (recalculado em ngOnInit apos carregar moeda).
  precoDeVendaStr = '0';
  custoStr = '';

  // Mapa de id da unidade de medida → casas decimais (para campos de estoque)
  private unidadeMedidaCasasDecimaisMap: Map<number, number> = new Map();
  casasDecimaisEstoque: number = 0;
  casasDecimaisEstoqueSaldo: number = 0; // casas decimais para o dialog de saldo

  // Combos do formulario
  unidadesMedida: { label: string; value: number }[] = [];
  grupos: { label: string; value: number }[] = [];
  subGrupos: { label: string; value: number }[] = [];
  marcas: { label: string; value: number }[] = [];
  fornecedores: { label: string; value: number }[] = [];
  habilitarMiniShopping: { label: string; value: boolean }[] = [];
  permiteImprimir: { label: string; value: boolean }[] = [];

  tiposDeDesconto = Object.values(TipoDeDesconto).map((value) => ({
    label: value,
    value,
  }));

  tipoDeItensOptions: { value: string; label: string }[] = [];
  tipoDeImpostoOptions: { value: string; label: string }[] = [];

  // Config da empresa
  habilitarMiniShoppingEmpresaConfig: boolean = false;
  imprimirItensDeAcordoImpressoraEmpresaConfig: boolean = false;
  /** Gate dos campos de impressao de PEDIDO: basta uma das duas origens estar ativa. */
  imprimirPedidoAutomaticoEmpresaConfig: boolean = false;
  /** Impressora de pedido padrao da empresa, valor inicial do item novo (espelha o app). */
  impressoraPadraoPedidoEmpresaConfig: number | null = null;

  // Upload / preview
  imageBase64: string | null = null;
  imagePreviewUrl: string | null = null;
  fileName: string | null = null;
  imagemAlterada = false;

  // Lightbox (expansao da imagem na edicao)
  imagemExpandida = false;

  // Variantes de preco (locais ao criar, salvamento manual ao editar)
  variantes: VarianteDePrecoDeItemDTO[] = [];

  // Grupos de modificadores (locais ao criar, salvamento manual ao editar)
  gruposModificadores: GrupoDeModificadorDePrecoDeItemDTO[] = [];

  // Dialog de confirmacao de remocao de grupo de modificador
  mostrarDialogRemoverGrupo = false;
  grupoParaRemover: { index: number; grupo: GrupoDeModificadorDePrecoDeItemDTO } | null = null;
  mensagemRemoverGrupo = '';

  // Flags de salvamento manual (modo edicao)
  salvandoVariantes = false;
  salvandoModificadores = false;

  /** Flag para evitar limpar subgrupo durante carga do produto */
  private isLoadingProduto = false;

  // =========================================================================
  // Traducoes
  // =========================================================================
  texts: any = {
    titulo: '',
    subtitulo: '',
    aba_cadastro: '',
    aba_lista: '',
    titulo_editar: '',
    titulo_criar: '',
    lista_de_produtos: '',
    subtitulo_produtos: '',
    nenhum_produto: '',
    ajuste_filtros: '',
    carregando: '',
    confirmar_exclusao: '',
    confirmacao: '',
    produto_excluido_sucesso: '',
    erro_ao_excluir_produto: '',
    atualizar_quantidade_do_produto: '',
    filtrado_por_fornecedor: '',
    limpar_filtro: '',
  };

  labels: any = {
    imagem: '', codigo_item: '', nome: '', grupo: '',
    sub_grupo: '', marca: '', saldo: '',
    preco_de_custo: '', preco_de_venda: '',
    acoes: '', itens_por_pagina: '',
  };

  placeholders: any = {
    pesquisar: '',
  };

  botoes: any = {
    novo_produto: '',
    salvar: '', cancelar: '', confirmar: '', atualizar: '', limpar: '',
  };

  tooltips: any = {
    editar: '', excluir: '',
    atualizar_saldo: '',
    gerar_etiqueta: '',
    extrato_estoque: '',
    ajuste_estoque: '',
    mais_acoes: '',
    saldo_readonly: '',
    item_de_sistema_hint: '',
    compartilhar: '',
    compartilhar_indisponivel: '',
  };

  private langChangeSub!: Subscription;

  constructor(
    private produtosService: ProdutosService,
    private empresaConfigService: EmpresaConfigService,
    private unidadeMedidaService: UnidadeMedidaDeItemService,
    private grupoService: GrupoService,
    private subGrupoService: SubGrupoService,
    private marcaService: MarcaService,
    private pessoaService: PessoaService,
    private impressoraService: ImpressoraService,
    private produtoVarianteModificadorService: ProdutoVarianteModificadorService,
    private messageService: MessageService,
    private router: Router,
    private route: ActivatedRoute,
    private translate: TranslateService,
    private ajusteEstoqueService: AjusteEstoqueService,
    private apiService: ApiService,
  ) { }

  ngOnInit(): void {
    // No modo embutido a empresa e a do CLIENTE (contexto), nao a do contador (sessao).
    this.idEmpresa = this.embutido
      ? String(this.empresaContextoId ?? '')
      : (sessionStorage.getItem('id') || '');
    this.loadColumnPreferences();
    this.loadTranslations();
    this.carregarConfiguracaoEmpresa();

    // Carregar combos do formulario
    this.loadFormDropdowns();
    // No modo embutido NAO carregamos as flags de configuracao: elas vem de
    // /configuracaoEmpresa/sessao, que responde pela empresa do CONTADOR (mesma
    // causa do bug de moeda). Os defaults `false` degradam com seguranca —
    // "Vender no Mini Shop" fica desabilitado e os campos de impressora ocultos,
    // em vez de refletirem a configuracao do contador na tela do cliente.
    if (!this.embutido) {
      this.loadEmpresaConfig();
    }

    this.buildTipoDeItensOptions();
    this.buildTipoDeImpostoOptions();
    this.buildGeneroOpcoes();
    this.preSelectTipoItem();

    // No modo embutido os query params da rota sao do Operar Cliente (aba/clienteId),
    // nao deste componente: ignora-os e apenas carrega a lista.
    if (this.embutido) {
      this.loadProdutos();
    } else {
      // Verificar filtro por fornecedor via queryParam
      const fornecedorIdParam = this.route.snapshot.queryParamMap.get('fornecedorId');
      if (fornecedorIdParam) {
        this.fornecedorId = Number(fornecedorIdParam);
        this.loadProdutosByFornecedor();
      } else {
        this.loadProdutos();
      }

      // Verificar se deve abrir edicao direta via queryParam ?edit=ID
      const editParam = this.route.snapshot.queryParamMap.get('edit');
      if (editParam) {
        const editId = Number(editParam);
        if (editId) {
          this.editarProduto(editId);
        }
      }

      // Verificar se deve abrir diretamente na aba Cadastro via queryParam ?aba=cadastro
      const abaParam = this.route.snapshot.queryParamMap.get('aba');
      if (abaParam === 'cadastro') {
        this.activeTabIndex = 0;
      }
    }

    // Monitorar mudanca de idioma
    this.langChangeSub = this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
      this.loadTranslations();
      this.loadFormTranslate();
      this.buildTipoDeItensOptions();
      this.buildTipoDeImpostoOptions();
      this.preSelectTipoItem();
    });
  }

  ngOnDestroy(): void {
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
  }

  // =========================================================================
  // Propriedades computed (moeda do formulario)
  // =========================================================================

  get locale(): string {
    // Mantem sincronizado com `localeMoeda` (carregado da empresa). Default
    // pt-BR para evitar flash visual com locale errado antes da API responder.
    return this.localeMoeda || 'pt-BR';
  }

  get currencyCode(): string {
    // Mantem sincronizado com `moedaPadrao` (ISO: BRL/PYG/USD/ARS). Default
    // BRL para evitar mostrar prefixo de moeda errada antes da API responder.
    return (this.moedaPadrao || 'BRL').toUpperCase();
  }

  get currencySymbol(): string {
    // Simbolo curto da moeda exibido nos inputs monetarios (preco, custo,
    // variantes e modificadores). Reflete a moeda padrao da empresa logada.
    switch (this.currencyCode) {
      case 'BRL': return 'R$';
      case 'USD': return '$';
      case 'ARS': return '$';
      case 'PYG': return 'Gs';
      default: return 'R$';
    }
  }

  get casasDecimaisDaMoeda(): number {
    // Casas decimais derivadas de `digitsMoeda` ('1.0-0' = 0, '1.2-2' = 2).
    // Fallback para 2 (Real) ate a API responder.
    return this.digitsMoeda === '1.0-0' ? 0 : 2;
  }

  // =========================================================================
  // Traducoes
  // =========================================================================

  onTabChange(event: { index: number }): void {
    const isEdicao = !!this.form.id;
    if (event.index === 1) {
      this.texts.titulo = this.translate.instant('produtos.texts.titulo_nova');
      this.texts.subtitulo = this.translate.instant('produtos.texts.subtitulo_lista');
    } else {
      this.texts.titulo = this.translate.instant(
        isEdicao ? 'produtos.texts.titulo_editar' : 'produtos.texts.titulo_nova'
      );
      this.texts.subtitulo = this.translate.instant(
        isEdicao ? 'produtos.texts.subtitulo_editar' : 'produtos.texts.subtitulo_nova'
      );
    }
  }

  private loadTranslations(): void {
    const isListaAtiva = this.activeTabIndex === 1;
    const isEdicao = !!this.form.id;
    this.texts = {
      titulo: this.translate.instant(
        isListaAtiva
          ? 'produtos.texts.titulo_nova'
          : isEdicao
            ? 'produtos.texts.titulo_editar'
            : 'produtos.texts.titulo_nova'
      ),
      subtitulo: this.translate.instant(
        isListaAtiva
          ? 'produtos.texts.subtitulo_lista'
          : isEdicao
            ? 'produtos.texts.subtitulo_editar'
            : 'produtos.texts.subtitulo_nova'
      ),
      aba_cadastro: this.translate.instant('produtos.texts.aba_cadastro'),
      aba_lista: this.translate.instant('produtos.texts.aba_lista'),
      titulo_editar: this.translate.instant('produtos.texts.titulo_editar'),
      titulo_criar: this.translate.instant('produtos.texts.adicionar_produto'),
      lista_de_produtos: this.translate.instant('produtos.texts.lista_de_produtos'),
      subtitulo_produtos: this.translate.instant('produtos.texts.subtitulo_produtos'),
      nenhum_produto: this.translate.instant('produtos.texts.nenhum_produto_encontrado'),
      ajuste_filtros: this.translate.instant('produtos.texts.ajuste_filtros'),
      carregando: this.translate.instant('produtos.texts.carregando'),
      confirmar_exclusao: this.translate.instant('produtos.texts.confirmar_exclusao'),
      confirmacao: this.translate.instant('produtos.texts.confirmacao'),
      produto_excluido_sucesso: this.translate.instant('produtos.texts.produto_excluido_sucesso'),
      erro_ao_excluir_produto: this.translate.instant('produtos.texts.erro_ao_excluir_produto'),
      atualizar_quantidade_do_produto: this.translate.instant('produtos.texts.atualizar_quantidade_do_produto'),
      filtrado_por_fornecedor: this.translate.instant('produtos.texts.filtrado_por_fornecedor'),
      limpar_filtro: this.translate.instant('produtos.texts.limpar_filtro'),
    };

    this.labels = {
      imagem: this.translate.instant('produtos.labels.imagem'),
      codigo_item: this.translate.instant('produtos.labels.codigo_item'),
      codigo_de_barras: this.translate.instant('produtos.labels.codigo_de_barras'),
      nome: this.translate.instant('produtos.labels.nome'),
      grupo: this.translate.instant('produtos.labels.grupo'),
      sub_grupo: this.translate.instant('produtos.labels.sub_grupo'),
      marca: this.translate.instant('produtos.labels.marca'),
      saldo: this.translate.instant('produtos.labels.saldo'),
      preco_de_custo: this.translate.instant('produtos.labels.preco_de_custo'),
      preco_de_venda: this.translate.instant('produtos.labels.preco_de_venda'),
      acoes: this.translate.instant('produtos.labels.acoes'),
      itens_por_pagina: this.translate.instant('produtos.labels.itens_por_pagina'),
    };

    this.placeholders = {
      pesquisar: this.translate.instant('produtos.placeholders.pesquisar'),
    };

    this.botoes = {
      novo_produto: this.translate.instant('produtos.botoes.adicionar_produto'),
      salvar: this.translate.instant('produtos.botoes.salvar'),
      cancelar: this.translate.instant('produtos.botoes.cancelar'),
      confirmar: this.translate.instant('produtos.botoes.confirmar'),
      atualizar: this.translate.instant('produtos-edit.botoes.atualizar'),
      limpar: this.translate.instant('produtos.botoes.limpar'),
    };

    this.tooltips = {
      editar: this.translate.instant('produtos.tooltips.editar'),
      excluir: this.translate.instant('produtos.tooltips.excluir'),
      atualizar_saldo: this.translate.instant('produtos.texts.atualizar_quantidade_do_produto'),
      gerar_etiqueta: this.translate.instant('produtos.tooltips.gerar_etiqueta'),
      extrato_estoque: this.translate.instant('produtos.tooltips.extrato_estoque'),
      ajuste_estoque: this.translate.instant('produtos.tooltips.ajuste_estoque'),
      mais_acoes: this.translate.instant('produtos.tooltips.mais_acoes'),
      saldo_readonly: this.translate.instant('produtos.tooltips.saldo_readonly'),
      item_de_sistema_hint: this.translate.instant('produtos.tooltips.item_de_sistema_hint'),
      compartilhar: this.translate.instant('produtos.tooltips.compartilhar'),
      compartilhar_indisponivel: this.translate.instant('produtos.tooltips.compartilhar_indisponivel'),
    };

    this.loadFormTranslate();
  }

  private loadFormTranslate(): void {
    this.translate.get(['produtos.labels.sim', 'produtos.labels.nao']).subscribe(translations => {
      this.habilitarMiniShopping = [
        { label: translations['produtos.labels.sim'], value: true },
        { label: translations['produtos.labels.nao'], value: false }
      ];
      this.permiteImprimir = [
        { label: translations['produtos.labels.sim'], value: true },
        { label: translations['produtos.labels.nao'], value: false }
      ];
    });
  }

  // =========================================================================
  // Config empresa
  // =========================================================================

  /**
   * Carrega a moeda padrao da empresa logada (ISO, locale e casas decimais)
   * para que os inputs monetarios (preco/custo/variantes/modificadores) exibam
   * prefixo e formato conforme a configuracao da empresa (R$ para BRL,
   * $ para USD/ARS, Gs para PYG).
   *
   * Usa `EmpresaConfigService.getSessao()` (endpoint cacheado via shareReplay)
   * — mesmo padrao adotado em /pedidos e /vendas para evitar requisicoes
   * duplicadas e respeitar a moeda da sessao do usuario logado.
   */
  carregarConfiguracaoEmpresa(): void {
    // Modo EMBUTIDO (aba "Gestão de itens" do Operar Cliente): a moeda ja chega
    // pronta por @Input, resolvida pelo pai sob o contexto do cliente. NAO
    // consultar /configuracaoEmpresa/sessao aqui — ver o comentario de
    // `moedaCliente`: essa rota responde pela empresa do CONTADOR.
    if (this.embutido) {
      // O Mini Shop do CLIENTE nao e resolvivel sob contexto (o endpoint enxuto
      // do contador nao expoe `urlDoMiniShopping`). Slug vazio mantem a acao de
      // compartilhar desativada, em vez de vazar a loja do CONTADOR.
      this.slugLojaMiniShop = '';
      this.aplicarMoedaDaEmpresa(this.moedaCliente);
      return;
    }

    this.empresaConfigService.getSessao().subscribe({
      next: (res) => {
        // Slug da loja no Mini Shop (usado para montar a URL publica do item
        // na acao de compartilhar). Pode vir vazio se a loja ainda nao
        // configurou o Mini Shop.
        this.slugLojaMiniShop = (res?.urlDoMiniShopping || '').trim();

        this.aplicarMoedaDaEmpresa(res?.moeda);
      },
      error: () => {
        // Fallback: mantem defaults Real (BRL).
        this.moedaPadrao = 'BRL';
        this.localeMoeda = 'pt-BR';
        this.digitsMoeda = '1.2-2';
      },
    });
  }

  /**
   * Deriva `moedaPadrao` (ISO), `localeMoeda` e `digitsMoeda` a partir da moeda
   * da empresa. Fonte unica dos dois caminhos: sessao (modo normal) e @Input
   * `moedaCliente` (modo embutido), para os dois nunca divergirem.
   */
  private aplicarMoedaDaEmpresa(moeda: MoedaDTO | null | undefined): void {
    const codigoOperacao =
      moeda?.codigoOperacao ??
      (moeda?.enum === 'REAL'
        ? 'BRL'
        : moeda?.enum === 'DOLAR'
          ? 'USD'
          : moeda?.enum === 'GUARANI'
            ? 'PYG'
            : 'BRL');
    this.moedaPadrao = codigoOperacao;

    const rawLocal = moeda?.pais?.local || 'pt_BR';
    this.localeMoeda = String(rawLocal).replace('_', '-');

    const casas = Number(moeda?.casasDecimais ?? (codigoOperacao === 'PYG' ? 0 : 2));
    this.digitsMoeda = casas === 0 ? '1.0-0' : '1.2-2';

    // Reformatar precoDeVendaStr com a moeda correta — somente em modo criacao
    // (em edicao, o valor e carregado em loadProdutoParaEdicao).
    if (!this.form.id) {
      this.precoDeVendaStr = this.formatarValorParaExibicao(this.form.precoDeVenda);
    }
  }

  /**
   * Carrega a configuracao COMPLETA da empresa, e nao a de sessao.
   *
   * O `ConfiguracaoEmpresaSessaoDTO` do backend nao expoe nenhuma das flags de impressao:
   * `imprimirItensDeAcordoImpressora` chegava `undefined`, o `|| false` a zerava e os campos
   * de impressao do item nunca renderizavam na tela. O app mobile ja resolve assim, lendo a
   * configuracao completa em vez da de sessao.
   *
   * A cadeia `getSessao() -> id -> /configuracaoEmpresa/{id}` espelha o que
   * `configuracao-da-empresa` e `imprimir-pedido-dialog` ja fazem. O `getSessao()` e cacheado
   * (`empresa.service.ts`), entao o custo real e de UM request a mais ao abrir a tela.
   *
   * TODO: quando as flags de impressao forem expostas no `ConfiguracaoEmpresaSessaoDTO`,
   * voltar a ler so a sessao e eliminar o request extra.
   */
  loadEmpresaConfig(): void {
    this.empresaConfigService
      .getSessao()
      .pipe(
        // Sem id na sessao nao ha como buscar a completa; segue com o que veio (degrada para
        // o comportamento antigo, sem os campos de impressao, em vez de quebrar a tela).
        switchMap((sessao: any) =>
          sessao?.id ? this.empresaConfigService.get(`/configuracaoEmpresa/${sessao.id}`) : of(sessao)
        ),
        catchError((err) => {
          console.error('Erro ao carregar configuracao da empresa:', err);
          return of(null);
        })
      )
      .subscribe((empresa: any) => {
        if (!empresa) return;
        this.habilitarMiniShoppingEmpresaConfig = empresa.habilitarMiniShopping || false;
        this.imprimirItensDeAcordoImpressoraEmpresaConfig = empresa.imprimirItensDeAcordoImpressora || false;
        this.imprimirPedidoAutomaticoEmpresaConfig =
          empresa.imprimirAutomaticoPedidoLocal === true || empresa.imprimirAutomaticoPedidoMinishop === true;
        this.impressoraPadraoPedidoEmpresaConfig = empresa.impressoraPadraoPedido?.id ?? null;

        // O `form` e criado no inicializador do campo, antes desta resposta chegar, entao o
        // default de `createEmptyForm` ainda nao tinha a impressora da empresa. Aplica aqui
        // para quem abre a tela e vai direto ao cadastro, sem passar por Novo/Limpar.
        // So em item NOVO e so quando o usuario ainda nao escolheu nada.
        if (!this.form.id && this.form.impressoraPadraoPedidoId == null) {
          this.form.impressoraPadraoPedidoId = this.impressoraPadraoPedidoEmpresaConfig ?? undefined;
        }
      });
  }

  // =========================================================================
  // Carregar Dropdowns do Formulario
  // =========================================================================

  private loadFormDropdowns(): void {
    this.loadUnidadesMedida();
    this.loadGrupos();
    this.loadSubGrupos();
    this.loadMarcas();
    this.loadFornecedores();
  }

  loadUnidadesMedida(): void {
    this.unidadeMedidaService.get('/unidaDeMedidaDeItemEmpresa', { page: 0, size: 1000, sort: 'id,asc' }).subscribe({
      next: (response: any) => {
        this.unidadeMedidaCasasDecimaisMap.clear();
        this.unidadesMedida = (response.content || []).map((item: any) => {
          const id = item.unidadeMedidaDeItemEmpresa?.id ?? item.id ?? 0;
          const casas = item.unidadeMedidaDeItemEmpresa?.casasDecimais ?? item.casasDecimais ?? 0;
          this.unidadeMedidaCasasDecimaisMap.set(id, casas);
          return {
            label: item.unidadeMedidaDeItemEmpresa?.nome ?? item.nome,
            value: id,
          };
        });
        this.preencherUnidadeMedidaPadrao();
      },
      error: () => {
        this.unidadesMedida = [];
      },
    });
  }

  /** Pre-preenche o campo de unidade de medida com a unidade padrao da empresa.
   *
   * Replica a logica de fallback do mobile:
   *  1. Tenta GET /unidaDeMedidaDeItemEmpresa/unidadeMedidaPadrao
   *  2. Se 404/erro: busca GET /configuracaoGeral → content[0].unidadeMedidaPadrao (string com nome)
   *  3. Com o nome, busca GET /unidadeMedidaItem/buscarPorNome → content[0]
   *  4. Aplica o resultado fazendo match pelo ID global do catalogo
   */
  private preencherUnidadeMedidaPadrao(): void {
    // Nao sobrescreve se o usuario ja selecionou uma unidade ou esta editando
    if (this.form.unidadeMedidaDeItem_id && (this.form.unidadeMedidaDeItem_id as any) !== 0) {
      return;
    }

    this.unidadeMedidaService.buscarUnidadePadrao().pipe(
      catchError(() =>
        // Fallback nivel 1: /configuracaoGeral
        this.unidadeMedidaService.buscarConfiguracaoGeral().pipe(
          switchMap((cfg: any) => {
            const nome = cfg?.content?.[0]?.unidadeMedidaPadrao;
            if (!nome) return of(null);
            // Fallback nivel 2: /unidadeMedidaItem/buscarPorNome
            return this.unidadeMedidaService.buscarPorNome(nome).pipe(
              catchError(() => of(null))
            );
          }),
          catchError(() => of(null))
        )
      )
    ).subscribe({
      next: (unidadePadrao: any) => {
        if (!unidadePadrao) return;
        this.aplicarUnidadePadrao(unidadePadrao);
      },
      error: () => {
        // Unidade padrao e opcional — falha silenciosa
      },
    });
  }

  /**
   * Forca a unidade de medida para "Unidade" quando o item e SERVICO.
   * Replica o comportamento do mobile (gerenciar_item_screen.dart linhas 129-145).
   * Em caso de erro, falha silenciosa (mantem a unidade atual).
   */
  private forcarUnidadeMedidaParaServico(): void {
    this.unidadeMedidaService.buscarPorNome('Unidade').pipe(
      catchError(() => of(null)),
    ).subscribe({
      next: (resultado: any) => {
        if (!resultado) return;
        this.aplicarUnidadePadrao(resultado);
      },
    });
  }

  /**
   * Aplica a unidade padrao resolvida ao formulario.
   *
   * O value em this.unidadesMedida e sempre o ID global do catalogo
   * (unidadeMedidaDeItemEmpresa.id), conforme mapeado em loadUnidadesMedida().
   *
   * - Quando vem do endpoint principal: id global esta em unidadePadrao.unidadeMedidaDeItemEmpresa.id
   * - Quando vem do fallback buscarPorNome: id global esta em unidadePadrao.content[0].id
   *   (ou diretamente em unidadePadrao.id se o endpoint retornar objeto direto)
   */
  private aplicarUnidadePadrao(unidadePadrao: any): void {
    // Candidatos ao ID global (catalogo)
    const idGlobal = unidadePadrao?.unidadeMedidaDeItemEmpresa?.id;
    // buscarPorNome pode retornar paginado { content: [...] } ou objeto direto
    const idDoBuscarPorNome = unidadePadrao?.content?.[0]?.id ?? unidadePadrao?.id;

    const idPadrao = idGlobal ?? idDoBuscarPorNome;
    if (!idPadrao) return;

    const opcao = this.unidadesMedida.find((u) => u.value === idPadrao);
    if (opcao) {
      this.form.unidadeMedidaDeItem_id = opcao.value;
      this.atualizarCasasDecimaisEstoque(opcao.value);
      return;
    }

    // Fallback por nome caso o ID nao tenha batido
    const nome: string | undefined =
      unidadePadrao?.unidadeMedidaDeItemEmpresa?.nome ??
      unidadePadrao?.content?.[0]?.nome ??
      unidadePadrao?.nome;
    if (nome) {
      const opcaoPorNome = this.unidadesMedida.find(
        (u) => u.label?.toLowerCase() === nome.toLowerCase()
      );
      if (opcaoPorNome) {
        this.form.unidadeMedidaDeItem_id = opcaoPorNome.value;
        this.atualizarCasasDecimaisEstoque(opcaoPorNome.value);
      }
    }
  }

  loadGrupos(): void {
    this.grupoService.get('/grupo').subscribe({
      next: (response) => {
        this.grupos = response.content.map((grupo: { nome: string; id?: number }) => ({
          label: grupo.nome,
          value: grupo.id ?? 0,
        }));
      },
      error: () => {
        this.grupos = [];
      },
    });
  }

  loadSubGrupos(): void {
    this.subGrupoService.get('/subgrupo').subscribe({
      next: (response) => {
        this.subGrupos = response.content.map((subGrupo: { nome: string; id?: number }) => ({
          label: subGrupo.nome,
          value: subGrupo.id ?? 0,
        }));
      },
      error: () => {
        this.subGrupos = [];
      },
    });
  }

  loadMarcas(): void {
    this.marcaService.get('/marca').subscribe({
      next: (response) => {
        this.marcas = response.content.map((marca: { nome: string; id?: number }) => ({
          label: marca.nome,
          value: marca.id ?? 0,
        }));
      },
      error: () => {
        this.marcas = [];
      },
    });
  }

  loadFornecedores(): void {
    this.pessoaService.get('/pessoas/buscar?categoriaFornecedor=true').subscribe({
      next: (response) => {
        this.fornecedores = (response?.content || []).map((fornecedor) => ({
          label: fornecedor.nome,
          value: fornecedor.id ?? 0,
        }));
      },
      error: () => {
        this.fornecedores = [];
      },
    });
  }


  // =========================================================================
  // Tipo de Itens / Imposto (opcoes de dropdown)
  // =========================================================================

  private buildTipoDeItensOptions(): void {
    this.tipoDeItensOptions = Object.values(TipoItem).map(filtro => ({
      value: filtro,
      label: this.translate.instant(`tipo_de_item.${filtro}`)
    }));
  }

  private buildTipoDeImpostoOptions(): void {
    this.tipoDeImpostoOptions = Object.values(TipoDeImposto).map(filtro => ({
      value: filtro,
      label: this.translate.instant(`tipo_de_imposto.${filtro}`)
    }));
  }

  /**
   * Carrega as opcoes de genero a partir do backend (GET /enums/generoItem),
   * tornando o backend a fonte unica dos valores. O label e a chave i18n
   * derivada do valor (ex.: UNISSEX -> produtos.genero.unissex). Em caso de
   * erro mantem o fallback definido em generoOpcoes.
   */
  private buildGeneroOpcoes(): void {
    // O backend serializa o enum como objeto { enum, nome, ... } (LocalizableEnum).
    // Usamos o campo `enum` como value e derivamos a chave i18n do cliente.
    this.apiService.read<any[]>('/enums/generoItem').subscribe({
      next: (valores) => {
        if (Array.isArray(valores) && valores.length > 0) {
          this.generoOpcoes = valores.map(item => {
            const value = (item?.enum ?? item) as GeneroItem;
            return {
              value,
              label: `produtos.genero.${(value as string).toLowerCase()}`
            };
          });
        }
      },
      error: () => { /* mantem o fallback */ }
    });
  }

  /** Pre-seleciona "Mercadoria" como tipo de item padrao */
  private preSelectTipoItem(): void {
    const mercadoria = this.tipoDeItensOptions.find(o => o.value === TipoItem.MERCADORIA);
    if (mercadoria && !this.form.tipoItem?.value) {
      this.form.tipoItem = mercadoria as any;
    }
  }

  /** Converte o tipoItem vindo do backend para o formato do dropdown */
  private mapTipoItemToOption(tipoItem: any): any {
    if (!tipoItem) return this.tipoDeItensOptions.find(o => o.value === TipoItem.MERCADORIA) || null;
    const enumValue = typeof tipoItem === 'string' ? tipoItem : tipoItem?.enum;
    return this.tipoDeItensOptions.find(o => o.value === enumValue) || null;
  }

  getTipoItemLabel(): string {
    return this.form.tipoItem?.label || this.translate.instant('produtos.placeholders.selecionar_tipo_produto');
  }

  /**
   * Handler disparado quando o usuario troca o tipo do item no dropdown.
   * - SERVICO: zera campos de estoque/custo/fornecedor e forca unidade "Unidade".
   * - MERCADORIA: restaura a unidade padrao configurada para a empresa.
   */
  onTipoItemChange(): void {
    if (this.ehServico) {
      this.form.saldo = 0;
      this.form.estoqueMinimo = 0;
      this.form.custo = 0;
      this.custoStr = '';
      this.form.fornecedorPrincipal_id = undefined;
      this.forcarUnidadeMedidaParaServico();
    } else {
      // Volta para a unidade padrao da empresa.
      // Zera o campo antes para permitir que preencherUnidadeMedidaPadrao() re-execute
      // (ele tem guarda contra sobrescrever unidade ja preenchida).
      this.form.unidadeMedidaDeItem_id = 0;
      this.preencherUnidadeMedidaPadrao();
    }
  }

  getTipoImpostoLabel(): string {
    return this.tipoDeImpostoOptions.find(o => o.value === this.form.tipoDeImposto)?.label || this.translate.instant('produtos.placeholders.selecionar_tipo_imposto');
  }

  // =========================================================================
  // Tabela (Lista)
  // =========================================================================

  loadProdutos(page: number = 0, size: number = this.rows, sort: string = 'id,asc'): void {
    this.loading = true;
    this.imagensComErro = {};
    this.produtosService.get('/item', {
      page,
      size,
      sort,
    }).subscribe({
      next: (response) => {
        this.produtos = response.content;
        this.totalRecords = response.totalElements;
        this.first = page * size;
        this.loading = false;
      },
      error: (err) => {
        console.error('Erro ao carregar produtos:', err);
        this.produtos = [];
        this.totalRecords = 0;
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro_servidor'),
        });
      },
    });
  }

  findProdutosByNone(
    page: number = 0,
    size: number = this.rows,
    sort: string = 'id,asc',
  ) {
    let filter = '&saldo=TODOS';
    filter += `&buscaGeral=${this.searchNome}`;

    this.loading = true;
    this.imagensComErro = {};
    this.produtosService
      .get('/item/buscarPorFiltros', {
        page,
        size,
        sort,
        filter,
      })
      .subscribe({
        next: (response) => {
          this.produtos = response.content;
          this.totalRecords = response.totalElements;
          this.first = page * size;
          this.loading = false;
        },
        error: (error) => {
          console.error('Erro ao buscar produtos:', error);
          this.produtos = [];
          this.totalRecords = 0;
          this.loading = false;
        },
      });
  }

  onSearchChange(value: string): void {
    this.searchNome = value || '';
    if (this.searchNome.trim().length === 0) {
      this.loadProdutos();
    } else if (this.searchNome.trim().length >= 3) {
      this.first = 0;
      this.findProdutosByNone(0, this.rows, `${this.lastfield},${this.lastSort}`);
    }
  }

  search(): void {
    if (this.searchNome.trim().length === 0) {
      this.loadProdutos();
      return;
    }
    this.findProdutosByNone(0, this.rows, `${this.lastfield},${this.lastSort}`);
  }

  onPageChange(event: any): void {
    const page = event.page;
    const size = event.rows;
    const sort = `${this.lastfield},${this.lastSort}`;

    if (this.fornecedorId) {
      this.loadProdutosByFornecedor(page, size, sort);
      return;
    }
    if (this.searchNome === '') {
      this.loadProdutos(page, size, sort);
      return;
    }
    this.findProdutosByNone(page, size, sort);
  }

  onRowsPerPageChange(): void {
    if (this.rows === this._lastRows) return;
    this._lastRows = this.rows;
    this.first = 0;
    const sort = `${this.lastfield},${this.lastSort}`;

    if (this.fornecedorId) {
      this.loadProdutosByFornecedor(0, this.rows, sort);
      return;
    }
    if (this.searchNome === '') {
      this.loadProdutos(0, this.rows, sort);
      return;
    }
    this.findProdutosByNone(0, this.rows, sort);
  }

  customSort(event: SortEvent): void {
    let sort = 'desc';
    if (event.order === 1) {
      sort = 'asc';
    }

    if (this.lastfield === event.field && this.lastSort === sort) {
      return;
    }

    this.lastfield = String(event.field);
    this.lastSort = String(sort);
    const sortStr = `${this.lastfield},${this.lastSort}`;

    if (this.fornecedorId) {
      this.loadProdutosByFornecedor(0, this.rows, sortStr);
      return;
    }
    if (this.searchNome === '') {
      this.loadProdutos(0, this.rows, sortStr);
      return;
    }
    this.findProdutosByNone(0, this.rows, sortStr);
  }

  // =========================================================================
  // Filtro por fornecedor
  // =========================================================================

  loadProdutosByFornecedor(page: number = 0, size: number = this.rows, sort: string = 'id,asc'): void {
    if (!this.fornecedorId) return;

    const filter = `&saldo=TODOS&fornecedorId=${this.fornecedorId}`;
    this.loading = true;
    this.imagensComErro = {};
    this.produtosService
      .get('/item/buscarPorFiltros', {
        page,
        size,
        sort,
        filter,
      })
      .subscribe({
        next: (response) => {
          this.produtos = response.content;
          this.totalRecords = response.totalElements;
          this.first = page * size;
          this.loading = false;

          if (this.produtos.length > 0) {
            const primeiro = this.produtos[0] as any;
            if (primeiro.fornecedorPrincipal?.nome) {
              this.fornecedorNome = primeiro.fornecedorPrincipal.nome;
            }
          }
        },
        error: (err) => {
          console.error('Erro ao carregar produtos por fornecedor:', err);
          this.produtos = [];
          this.totalRecords = 0;
          this.loading = false;
        },
      });
  }

  limparFiltroFornecedor(): void {
    this.fornecedorId = null;
    this.fornecedorNome = '';
    this.router.navigate(['/produtos']);
    this.loadProdutos();
  }

  // =========================================================================
  // Navegacao entre abas
  // =========================================================================

  novoProduto(): void {
    this.form = this.createEmptyForm();
    // form.precoDeVenda = 0 -> exibir "0" formatado conforme a moeda
    this.precoDeVendaStr = this.formatarValorParaExibicao(this.form.precoDeVenda);
    this.custoStr = '';
    this.variantes = [];
    this.gruposModificadores = [];
    this.isFormSubmitted = false;
    this.imageBase64 = null;
    this.imagePreviewUrl = null;
    this.fileName = null;
    this.imagemAlterada = false;
    this.casasDecimaisEstoque = 0;
    this.resetarPromocaoNovidade();
    this.preSelectTipoItem();
    this.preencherUnidadeMedidaPadrao();
    this.activeTabIndex = 0;

    // Restaurar header para modo criacao
    this.texts.titulo = this.translate.instant('produtos.texts.titulo_nova');
    this.texts.subtitulo = this.translate.instant('produtos.texts.subtitulo_nova');
  }

  editarProduto(id: number): void {
    this.loadProdutoParaEdicao(id);
    this.activeTabIndex = 0;

    // Atualizar header para modo edicao
    this.texts.titulo = this.translate.instant('produtos.texts.titulo_editar');
    this.texts.subtitulo = this.translate.instant('produtos.texts.subtitulo_editar');
  }

  limparFormulario(): void {
    this.form = this.createEmptyForm();
    this.precoDeVendaStr = this.formatarValorParaExibicao(this.form.precoDeVenda);
    this.custoStr = '';
    this.variantes = [];
    this.gruposModificadores = [];
    this.isFormSubmitted = false;
    this.imageBase64 = null;
    this.imagePreviewUrl = null;
    this.fileName = null;
    this.casasDecimaisEstoque = 0;
    this.resetarPromocaoNovidade();
    this.preSelectTipoItem();
    this.preencherUnidadeMedidaPadrao();

    // Restaurar header para modo criacao
    this.texts.titulo = this.translate.instant('produtos.texts.titulo_nova');
    this.texts.subtitulo = this.translate.instant('produtos.texts.subtitulo_nova');
  }

  // =========================================================================
  // CRUD - Deletar
  // =========================================================================

  deletar(id: number): void {
    // Modo embutido (contexto do contador): exclusao fora de escopo (o backend nem
    // permite DELETE /item sob contexto). Bloqueio defensivo.
    if (this.embutido) {
      return;
    }
    // Guarda defensivo: itens de sistema nao podem ser excluidos nem inativados.
    // Alem de ocultar os botoes no template, bloqueamos aqui por seguranca
    // (atalho de teclado, chamada programatica, etc.).
    const produto = this.produtos?.find(p => p.id === id);
    if (produto?.itemDeSistema) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('common.atencao'),
        detail: this.translate.instant('produtos.texts.erro_item_de_sistema_nao_pode_ser_excluido'),
      });
      return;
    }
    this.deleteItemId = id;
    this.deleteDialogVisible = true;
  }

  confirmarExclusao(): void {
    if (!this.deleteItemId) return;

    this.produtosService.delete(`/item/${this.deleteItemId}`).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.texts.confirmacao,
          detail: this.texts.produto_excluido_sucesso,
        });
        this.deleteDialogVisible = false;
        this.deleteItemId = null;
        this.loadProdutos();
      },
      error: (err) => {
        // Trata erro 422 especifico de item de sistema vindo do backend
        const code = err?.error?.code ?? err?.error?.error ?? null;
        if (code === 'item_de_sistema_nao_pode_ser_excluido') {
          this.messageService.add({
            severity: 'warn',
            summary: this.translate.instant('common.atencao'),
            detail: this.translate.instant('produtos.texts.erro_item_de_sistema_nao_pode_ser_excluido'),
          });
        }
        this.deleteDialogVisible = false;
        this.deleteItemId = null;
      },
    });
  }

  // =========================================================================
  // Dialog Saldo (atualizar saldo da lista)
  // =========================================================================

  autualizarProduto(pruduto: ProdutosDTO | null) {
    if (!pruduto) return;
    const payload = this.mapProdutoListaToPayload(pruduto);

    this.produtosService.put(`/item/${pruduto.id}`, payload).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.texts.confirmacao,
          detail: this.translate.instant('lista-de-produtos.mensagens.atualizado_sucesso'),
        });
        this.produtoSelecionado = null;
      },
    });
  }

  showDialogSaldo(produto: ProdutosDTO) {
    // Ajuste de saldo usa /ajusteDeEstoque, fora do allowlist do contexto. Bloqueio embutido.
    if (this.embutido) {
      return;
    }
    this.produtoSelecionado = produto;
    this.saldoOriginal = produto.saldo ?? 0;
    // Buscar casas decimais da unidade do produto selecionado
    const unidadeId = (produto as any).unidadeMedidaDeItem?.id ?? (produto as any).unidadeMedidaDeItem_id ?? 0;
    this.casasDecimaisEstoqueSaldo = this.unidadeMedidaCasasDecimaisMap.get(unidadeId) ?? 0;
    this.displayDialogSaldo = true;
  }

  closeDialogSaldo() {
    this.displayDialogSaldo = false;
    this.loadProdutos();
  }

  salvarAjusteSaldo(): void {
    if (!this.produtoSelecionado) return;

    const novoSaldo = this.produtoSelecionado.saldo ?? 0;

    // Se saldo nao mudou, exibe info e retorna
    if (novoSaldo === this.saldoOriginal) {
      this.messageService.add({
        severity: 'info',
        summary: this.texts.confirmacao,
        detail: this.translate.instant('lista-de-produtos.mensagens.saldo_nao_alterado'),
      });
      return;
    }

    const diferenca = Math.abs(novoSaldo - this.saldoOriginal);
    const tipo = novoSaldo > this.saldoOriginal
      ? TipoOperacaoDeEstoque.ENTRADA
      : TipoOperacaoDeEstoque.SAIDA;

    const dto: AjusteDeEstoqueDTO = {
      tipoOperacaoDeEstoque: tipo,
      item_id: this.produtoSelecionado.id,
      quantidade: diferenca,
      emissao: new Date().toISOString().replace('Z', ''),
      tipoContaEstoque: TipoContaEstoque.NORMAL,
    };

    this.ajusteEstoqueService.post('/ajusteDeEstoque', dto).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.texts.confirmacao,
          detail: this.translate.instant('lista-de-produtos.mensagens.atualizado_sucesso'),
        });
        this.closeDialogSaldo();
      },
    });
  }

  // =========================================================================
  // Imagens (tabela)
  // =========================================================================

  onImageError(event: Event, produtoId: number): void {
    const img = event.target as HTMLImageElement;
    img.src = 'assets/images/profile_placeholder.png';
    img.style.opacity = '0.4';
    img.style.objectFit = 'contain';
    img.style.padding = '6px';
    this.imagensComErro[produtoId] = true;
  }

  onExpandedImageError(event: Event): void {
    (event.target as HTMLImageElement).src = 'assets/images/profile_placeholder.png';
  }

  produtoTemImagemValida(produto: ProdutosDTO): boolean {
    return !!produto.imagem && produto.imagem.trim().length > 0 && !this.imagensComErro[produto.id!];
  }

  getImagemUrl(imagem: string): string {
    if (!imagem) {
      return 'assets/images/profile_placeholder.png';
    }
    if (imagem.startsWith('http') || imagem.startsWith('data:image')) {
      return imagem;
    }
    return `${this.env.imgHost}/${this.idEmpresa}/item/${imagem}`;
  }

  openImageDialog(imagem: string, produtoId?: number) {
    if (!imagem || (produtoId !== undefined && this.imagensComErro[produtoId])) return;
    this.expandedImageUrl = this.getImagemUrl(imagem);
    this.displayDialogImage = true;
  }

  // =========================================================================
  // Dialog de Visualizacao de Imagens Secundarias (Tab Lista)
  // =========================================================================

  abrirImagensDoProduto(produto: ProdutosDTO): void {
    this.produtoSelecionadoParaImagens = produto;
  }

  fecharImagensDoProduto(): void {
    this.produtoSelecionadoParaImagens = null;
  }

  // Two-way binding do p-dialog de imagens. O p-dialog precisa de [(visible)]
  // para que o botao de fechar (X) e o Esc realmente ocultem o dialog; com
  // [visible]="true" fixo o PrimeNG reabria o dialog no proximo change detection.
  get imagensDialogVisible(): boolean {
    return !!this.produtoSelecionadoParaImagens;
  }

  set imagensDialogVisible(value: boolean) {
    if (!value) {
      this.produtoSelecionadoParaImagens = null;
    }
  }

  // =========================================================================
  // Dialog Etiqueta
  // =========================================================================

  abrirDialogEtiqueta(produto: ProdutosDTO): void {
    this.produtoEtiqueta = produto;
    this.displayDialogEtiqueta = true;
  }

  // =========================================================================
  // Navegacao para extrato/ajuste estoque
  // =========================================================================

  abrirExtratoEstoque(itemId: number): void {
    // Sairia do contexto do contador (rota propria). Bloqueio no modo embutido.
    if (this.embutido) {
      return;
    }
    this.router.navigate(['/extrato-estoque', itemId]);
  }

  navegarParaAjusteEstoque(itemId: number): void {
    if (this.embutido) {
      return;
    }
    this.router.navigate(['/ajuste-estoque', itemId]);
  }

  // =========================================================================
  // Menu de acoes (3 pontos)
  // =========================================================================

  toggleMenu(event: Event, produto: ProdutosDTO): void {
    this.menuItems = this.buildMenuItems(produto);
    this.menuAcoes.toggle(event);
  }

  private buildMenuItems(produto: ProdutosDTO): MenuItem[] {
    const items: MenuItem[] = [
      {
        label: this.tooltips.editar,
        icon: 'pi pi-pencil',
        styleClass: 'menu-item-mobile-only',
        command: () => this.editarProduto(produto.id!),
      },
    ];

    // Modo embutido (contexto do contador): apenas EDITAR. Excluir, estoque
    // (extrato/ajuste), etiqueta e mini shop ficam de fora (escopo add/edit +
    // acoes que sairiam do contexto).
    if (this.embutido) {
      return items;
    }

    // Oculta "Excluir" para itens de sistema (nao-excluiveis / nao-inativaveis)
    if (!produto.itemDeSistema) {
      items.push({
        label: this.tooltips.excluir,
        icon: 'pi pi-trash',
        styleClass: 'menu-item-mobile-only',
        command: () => this.deletar(produto.id!),
      });
    }

    items.push(
      { separator: true, styleClass: 'menu-item-mobile-only' },
      {
        label: this.tooltips.extrato_estoque,
        icon: 'pi pi-book',
        command: () => this.abrirExtratoEstoque(produto.id!),
      },
      {
        label: this.tooltips.ajuste_estoque,
        icon: 'pi pi-sliders-h',
        command: () => this.navegarParaAjusteEstoque(produto.id!),
      },
      {
        label: this.tooltips.gerar_etiqueta,
        icon: 'pi pi-barcode',
        command: () => this.abrirDialogEtiqueta(produto),
      },
    );

    // Compartilhar URL do Mini Shop. Fica ATIVO apenas quando o item tem
    // saldo > 0 E esta habilitado no Mini Shop (venderNoMiniShop === true).
    // Quando indisponivel, o item aparece DESABILITADO (com tooltip explicando)
    // em vez de sumir — para o usuario entender que existe mas nao esta
    // disponivel para este item.
    const podeCompartilhar = this.podeCompartilharItem(produto);
    items.push({
      label: this.tooltips.compartilhar,
      icon: 'pi pi-share-alt',
      disabled: !podeCompartilhar,
      tooltipOptions: podeCompartilhar
        ? undefined
        : { tooltipLabel: this.tooltips.compartilhar_indisponivel, tooltipPosition: 'top' },
      command: () => this.compartilharItem(produto),
    });

    return items;
  }

  // =========================================================================
  // Compartilhar URL do Mini Shop
  // =========================================================================

  /**
   * Regra de habilitacao da acao de compartilhar: o item precisa ter
   * saldo > 0 E estar habilitado no Mini Shop (venderNoMiniShop === true).
   */
  private podeCompartilharItem(produto: ProdutosDTO): boolean {
    return (produto?.saldo ?? 0) > 0 && produto?.venderNoMiniShop === true;
  }

  /**
   * Monta a URL publica do item no Mini Shop e copia para a area de
   * transferencia, exibindo um toast de confirmacao. Usa a Web Share API
   * quando disponivel (mobile) com fallback para copia (desktop).
   */
  compartilharItem(produto: ProdutosDTO): void {
    // Guarda defensivo: respeita a mesma regra do item desabilitado do menu.
    if (!this.podeCompartilharItem(produto)) {
      return;
    }

    const url = this.montarUrlMinishopItem(produto);
    if (!url) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('common.atencao'),
        detail: this.tooltips.compartilhar_indisponivel,
      });
      return;
    }

    if (navigator.share) {
      navigator
        .share({ title: produto.nome, url })
        .catch((err) => {
          // Cancelamento do usuario (AbortError) nao deve exibir erro nem fallback.
          if (err?.name === 'AbortError') {
            return;
          }
          this.copiarUrlParaClipboard(url);
        });
      return;
    }

    this.copiarUrlParaClipboard(url);
  }

  /**
   * Monta a URL publica do item no Mini Shop:
   *   {origin}/menu/{slugLoja}?categoria={slugCategoria}&item={slugItem}
   *
   * - Base: window.location.origin (a propria web serve a rota publica /menu/:slug).
   * - slugLoja: urlDoMiniShopping da empresa logada (carregado da sessao).
   * - slugCategoria: slug do nome do grupo (a "categoria" do item na tela).
   * - slugItem: slug do nome do item.
   *
   * Retorna string vazia quando faltar peca essencial (slug da loja ou slug do
   * item), evitando compartilhar uma URL invalida.
   */
  private montarUrlMinishopItem(produto: ProdutosDTO): string {
    const slugLoja = this.slugLojaMiniShop;
    const slugItem = slugificar(produto?.nome);
    if (!slugLoja || !slugItem) {
      return '';
    }

    const base = `${window.location.origin}/menu/${encodeURIComponent(slugLoja)}`;
    const params: string[] = [];

    const slugCategoria = slugificarCategoria(produto?.grupo?.nome);
    if (slugCategoria) {
      params.push(`categoria=${encodeURIComponent(slugCategoria)}`);
    }
    params.push(`item=${encodeURIComponent(slugItem)}`);

    return `${base}?${params.join('&')}`;
  }

  // =========================================================================
  // Compartilhar URL do Mini Shop — modo EDICAO (aba Cadastro)
  // =========================================================================

  /**
   * Monta um ProdutosDTO minimo a partir do item em edicao (this.form) para
   * reutilizar a mesma logica de compartilhar/montar URL usada na listagem.
   *
   * - nome/saldo: lidos diretamente do form.
   * - grupo.nome: o form so guarda grupo_id; resolvemos o nome via o dropdown
   *   de grupos (this.grupos: { label: nome, value: id }) para gerar o slug da
   *   categoria. Se nao resolver, a URL e montada sem a categoria (mesmo
   *   fallback de montarUrlMinishopItem).
   * - venderNoMiniShop: reflete o valor ATUAL do campo "Habilitar Mini Shop"
   *   (form.habilitarMiniShopping), que foi carregado de venderNoMiniShop na
   *   edicao e pode ter sido alterado pelo usuario.
   */
  private montarProdutoDaEdicao(): ProdutosDTO {
    const nomeGrupo = this.grupos.find((g) => g.value === this.form.grupo_id)?.label;
    return {
      ...this.form,
      grupo: nomeGrupo ? { id: this.form.grupo_id, nome: nomeGrupo } : this.form.grupo,
      venderNoMiniShop: this.form.habilitarMiniShopping === true,
    };
  }

  /**
   * Regra de habilitacao do botao de compartilhar no formulario de edicao.
   * Igual a da listagem (saldo > 0 E habilitado no Mini Shop), porem
   * considerando o estado ATUAL do form: saldo do item e o valor atual do
   * campo "Habilitar Mini Shop". So faz sentido em modo edicao (form.id).
   */
  podeCompartilharEdicao(): boolean {
    return !!this.form.id
      && (this.form.saldo ?? 0) > 0
      && this.form.habilitarMiniShopping === true;
  }

  /**
   * Compartilha a URL do Mini Shop do item em edicao reutilizando a mesma
   * acao da listagem (Web Share API + fallback de copia + toast).
   */
  compartilharItemEmEdicao(): void {
    if (!this.podeCompartilharEdicao()) {
      return;
    }
    this.compartilharItem(this.montarProdutoDaEdicao());
  }

  /**
   * Copia a URL para a area de transferencia e exibe um toast de confirmacao.
   * Prioriza navigator.clipboard; se indisponivel, usa fallback com textarea
   * temporaria + execCommand. Mesmo padrao usado no cardapio digital.
   */
  private copiarUrlParaClipboard(url: string): void {
    const notificarSucesso = () => {
      this.messageService.add({
        severity: 'success',
        summary: this.translate.instant('cardapio_digital.link_copiado'),
        life: 2000,
      });
    };

    if (navigator.clipboard?.writeText) {
      navigator.clipboard
        .writeText(url)
        .then(notificarSucesso)
        .catch(() => this.copiarUrlFallback(url, notificarSucesso));
      return;
    }

    this.copiarUrlFallback(url, notificarSucesso);
  }

  /** Fallback final de copia via textarea temporaria + execCommand. */
  private copiarUrlFallback(url: string, onSucesso: () => void): void {
    try {
      const textarea = document.createElement('textarea');
      textarea.value = url;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      onSucesso();
    } catch {
      // Falha silenciosa — nao quebra a tela.
    }
  }

  // =========================================================================
  // Colunas
  // =========================================================================

  isColumnVisible(key: string): boolean {
    const col = this.columns.find(c => c.key === key);
    return col ? col.visible : true;
  }

  get visibleColumnCount(): number {
    return this.columns.filter(c => c.visible).length;
  }

  onColumnsChange(columns: TableColumn[]): void {
    this.columns = [...columns];
  }

  private loadColumnPreferences(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const visibleKeys = JSON.parse(stored) as string[];
        const storedAll = localStorage.getItem(this.STORAGE_KEY + '-all');
        const allKnownKeys = storedAll
          ? new Set(JSON.parse(storedAll) as string[])
          : new Set(visibleKeys);

        this.columns.forEach(col => {
          if (!col.fixed) {
            if (allKnownKeys.has(col.key)) {
              col.visible = visibleKeys.includes(col.key);
            } else {
              col.visible = col.defaultVisible;
            }
          }
        });
      }
    } catch (e) {
      console.warn('Erro ao carregar preferencias de colunas:', e);
    }
  }

  // =========================================================================
  // Carregar produto para edicao
  // =========================================================================

  private loadProdutoParaEdicao(id: number): void {
    this.isLoadingProduto = true;
    this.produtosService.get(`/item/${id}`).subscribe({
      next: (response: any) => {
        // Guardar saldo original do backend para proteger contra edicao
        this.saldoOriginal = response.saldo ?? 0;

        this.form = {
          id: response.id,
          codigoItem: response.codigoItem,
          codigoFabricante: response.codigoFabricante,
          barras: response.barras,
          nome: response.nome,
          marca_id: response.marca?.id ?? response.marca_id,
          unidadeMedidaDeItem_id: response.unidadeMedidaDeItem?.id ?? response.unidadeMedidaDeItem_id,
          precoDeVenda: response.precoDeVenda,
          tipoDeImposto: response.tipoDeImposto?.enum ?? response.tipoDeImposto,
          custo: response.custo,
          grupo_id: response.grupo?.id ?? response.grupo_id,
          subGrupo_id: response.subGrupo?.id ?? response.subGrupo_id,
          // Saldo e estoque minimo defaultam para 0 caso a API retorne
          // null/undefined — zero e valor valido para esses campos.
          estoqueMinimo: response.estoqueMinimo ?? 0,
          tipoDeDesconto: response.tipoDeDesconto?.enum ?? response.tipoDeDesconto,
          desconto: response.desconto,
          fornecedorPrincipal_id: response.fornecedorPrincipal?.id ?? response.fornecedorPrincipal_id,
          status: response.status,
          saldo: response.saldo ?? 0,
          imagem: response.imagem,
          descricao: response.descricao || '',
          tipoItem: this.mapTipoItemToOption(response.tipoItem),
          habilitarMiniShopping: response.venderNoMiniShop === true,
          impressoraPadraoId: response.impressoraDTO?.id ?? response.impressoraPadraoId,
          permiteImprimir: response.permiteImprimir === true || response.permiteImprimir === 'true',
          // `GET /item/{id}` devolve `ItemDTO` (com os IDs prontos); o `impressoraPedidoDTO` so
          // aparece no `ItemConsultaDTO` das listagens. Aceita os dois para nao depender do
          // endpoint que alimentou o formulario.
          impressoraPadraoPedidoId: response.impressoraPedidoDTO?.id ?? response.impressoraPadraoPedidoId,
          permiteImprimirPedido: response.permiteImprimirPedido === true || response.permiteImprimirPedido === 'true',
          itemDeSistema: response.itemDeSistema === true,
        };

        // Promocao/Novidade — parse ISO local -> Date (simetrico a toLocalISOString).
        this.ehItemDeSistema = response.itemDeSistema === true;
        this.promocao = response.promocao === true;
        this.promocaoAteEmData = fromLocalISOString(response.promocaoAteEm);
        this.novidade = response.novidade === true;
        this.novidadeAteEmData = fromLocalISOString(response.novidadeAteEm);

        // Campos exibidos no shop: indicado (texto) e genero (enum).
        // O backend serializa o enum como objeto { enum, nome, ... }; extrai o
        // nome do enum para casar com o value do dropdown.
        this.indicado = response.indicado ?? '';
        this.genero = (response.genero as any)?.enum ?? response.genero ?? null;

        // Configurar preview de imagem
        if (this.form.imagem) {
          this.imagePreviewUrl = `${this.env.imgHost}/${this.idEmpresa}/item/${this.form.imagem}`;
        } else {
          this.imagePreviewUrl = null;
        }

        this.isFormSubmitted = false;
        this.imagemAlterada = false;

        // Formatar strings de preco para exibicao no input com mascara
        this.precoDeVendaStr = this.formatarValorParaExibicao(this.form.precoDeVenda);
        this.custoStr = this.formatarValorParaExibicao(this.form.custo);

        // Atualizar casas decimais do estoque com base na unidade carregada
        if (this.form.unidadeMedidaDeItem_id) {
          this.atualizarCasasDecimaisEstoque(this.form.unidadeMedidaDeItem_id as number);
        }

        // Carregar variantes e modificadores
        this.loadVariantes(id);
        this.loadGruposModificadores(id);

        // Permite limpeza de subgrupo apos carga inicial
        setTimeout(() => { this.isLoadingProduto = false; });
      },
      error: (err) => {
        this.isLoadingProduto = false;
        if (err?.status === 404) {
          this.showError(this.translate.instant('produtos-edit.mensagens.produto_nao_encontrado'));
          return;
        }
        this.showError(this.translate.instant('produtos-edit.mensagens.erro_carregar_produto'));
      },
    });
  }

  // =========================================================================
  // Variantes de preco
  // =========================================================================

  private loadVariantes(itemId: number): void {
    this.produtoVarianteModificadorService.getVariantes(itemId).subscribe({
      next: (variantes) => {
        this.variantes = variantes || [];
      },
      error: (err) => {
        if (err?.status === 404) {
          this.variantes = [];
          return;
        }
        this.showError(this.translate.instant('produtos-edit.mensagens.erro_carregar_variantes'));
      },
    });
  }

  adicionarVariante(): void {
    const itemId = this.form.id || 0;
    this.variantes.push({
      item_id: itemId,
      descricao: '',
      precoDeVenda: 0,
    });
    // Foco automatico no input da nova variante apos renderizacao
    setTimeout(() => {
      const inputs = this.varianteDescricaoInputs?.toArray();
      if (inputs?.length) {
        inputs[inputs.length - 1].nativeElement.focus();
      }
    });
  }

  removerVariante(index: number): void {
    const variante = this.variantes[index];
    if (variante.id) {
      // Modo edicao: deletar do backend
      this.produtoVarianteModificadorService.deleteVariante(variante.id).subscribe({
        next: () => {
          this.variantes.splice(index, 1);
        },
        error: () => {
          this.showError(this.translate.instant('produtos-edit.mensagens.erro_remover_variante'));
        },
      });
    } else {
      this.variantes.splice(index, 1);
    }
  }

  /** Salva TODAS as variantes de uma vez (modo edicao - botao manual) */
  salvarVariantes(): void {
    if (!this.form.id) return;

    const variantesValidas = this.variantes.filter(v => v.descricao?.trim());
    if (variantesValidas.length === 0) return;

    this.salvandoVariantes = true;
    const requests = variantesValidas.map(variante => {
      if (variante.id) {
        return this.produtoVarianteModificadorService.updateVariante(variante.id, variante);
      } else {
        return this.produtoVarianteModificadorService.createVariante({ ...variante, item_id: this.form.id! });
      }
    });

    forkJoin(requests).subscribe({
      next: (resultados) => {
        // Atualizar variantes locais com dados retornados (incluindo ids de novas)
        let idx = 0;
        for (let i = 0; i < this.variantes.length; i++) {
          if (this.variantes[i].descricao?.trim()) {
            Object.assign(this.variantes[i], resultados[idx]);
            idx++;
          }
        }
        this.salvandoVariantes = false;
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.sucesso'),
          detail: this.translate.instant('produtos-edit.mensagens.variantes_salvas'),
        });
      },
      error: () => {
        this.salvandoVariantes = false;
        this.showError(this.translate.instant('produtos-edit.mensagens.erro_salvar_variante'));
      },
    });
  }

  // =========================================================================
  // Grupos de modificadores
  // =========================================================================

  private loadGruposModificadores(itemId: number): void {
    this.produtoVarianteModificadorService.getGrupos(itemId).subscribe({
      next: (grupos) => {
        this.gruposModificadores = grupos || [];
      },
      error: (err) => {
        if (err?.status === 404) {
          this.gruposModificadores = [];
          return;
        }
        this.showError(this.translate.instant('produtos-edit.mensagens.erro_carregar_modificadores'));
      },
    });
  }

  adicionarGrupoModificador(): void {
    const itemId = this.form.id || 0;
    this.gruposModificadores.push({
      item_id: itemId,
      descricao: '',
      maximoDeModificadores: 1,
      minimoDeModificadores: 0,
      modificadores: [],
    });
    setTimeout(() => {
      const inputs = this.grupoDescricaoInputs?.toArray();
      if (inputs?.length) {
        inputs[inputs.length - 1].nativeElement.focus();
      }
    });
  }

  removerGrupoModificador(index: number): void {
    const grupo = this.gruposModificadores[index];

    // Se o grupo ainda nao foi salvo, remove direto sem confirmacao
    if (!grupo.id) {
      this.gruposModificadores.splice(index, 1);
      return;
    }

    const qtdOpcoes = grupo.modificadores.length;
    this.mensagemRemoverGrupo = qtdOpcoes > 0
      ? this.translate.instant('produtos-edit.mensagens.confirmar_remover_grupo_com_opcoes', { qtd: qtdOpcoes })
      : this.translate.instant('produtos-edit.mensagens.confirmar_remover_grupo');

    this.grupoParaRemover = { index, grupo };
    this.mostrarDialogRemoverGrupo = true;
  }

  cancelarRemocaoGrupo(): void {
    this.mostrarDialogRemoverGrupo = false;
    this.grupoParaRemover = null;
    this.mensagemRemoverGrupo = '';
  }

  confirmarRemocaoGrupoFinal(): void {
    if (this.grupoParaRemover) {
      this.executarRemocaoGrupo(this.grupoParaRemover.index, this.grupoParaRemover.grupo);
    }
    this.mostrarDialogRemoverGrupo = false;
    this.grupoParaRemover = null;
    this.mensagemRemoverGrupo = '';
  }

  private executarRemocaoGrupo(index: number, grupo: GrupoDeModificadorDePrecoDeItemDTO): void {
    const modificadoresComId = grupo.modificadores.filter(m => m.id);
    if (modificadoresComId.length > 0) {
      let deletados = 0;
      let erros = 0;
      modificadoresComId.forEach(mod => {
        this.produtoVarianteModificadorService.deleteModificador(mod.id!).subscribe({
          next: () => {
            deletados++;
            if (deletados + erros === modificadoresComId.length && erros === 0) {
              this.deletarGrupo(index, grupo.id!);
            }
          },
          error: () => {
            erros++;
            if (deletados + erros === modificadoresComId.length) {
              this.showError(this.translate.instant('produtos-edit.mensagens.erro_remover_grupo_modificador'));
            }
          },
        });
      });
    } else {
      this.deletarGrupo(index, grupo.id!);
    }
  }

  private deletarGrupo(index: number, grupoId: number): void {
    this.produtoVarianteModificadorService.deleteGrupo(grupoId).subscribe({
      next: () => {
        this.gruposModificadores.splice(index, 1);
      },
      error: () => {
        this.showError(this.translate.instant('produtos-edit.mensagens.erro_remover_grupo_modificador'));
      },
    });
  }

  /** Toggle: "Cliente deve escolher?" */
  onToggleObrigatorio(grupoIndex: number, obrigatorio: boolean): void {
    this.gruposModificadores[grupoIndex].minimoDeModificadores = obrigatorio ? 1 : 0;
  }

  /** Toggle: "Pode escolher mais de um?" */
  onToggleMultiplo(grupoIndex: number, multiplo: boolean): void {
    this.gruposModificadores[grupoIndex].maximoDeModificadores = multiplo ? 3 : 1;
  }

  /** Salva TODOS os grupos + modificadores em sequencia correta (modo edicao - botao manual) */
  salvarModificadores(): void {
    if (!this.form.id) return;

    const gruposValidos = this.gruposModificadores.filter(g => g.descricao?.trim());
    if (gruposValidos.length === 0) return;

    this.salvandoModificadores = true;
    this.salvarGruposModificadoresSequencialEdicao(gruposValidos, 0);
  }

  /** Salva grupos sequencialmente: primeiro cria novos, depois atualiza existentes, depois salva modificadores */
  private salvarGruposModificadoresSequencialEdicao(
    grupos: GrupoDeModificadorDePrecoDeItemDTO[],
    index: number
  ): void {
    if (index >= grupos.length) {
      this.salvandoModificadores = false;
      this.messageService.add({
        severity: 'success',
        summary: this.translate.instant('common.sucesso'),
        detail: this.translate.instant('produtos-edit.mensagens.modificadores_salvos'),
      });
      return;
    }

    const grupo = grupos[index];
    const modificadoresValidos = grupo.modificadores.filter(m => m.descricao?.trim());

    if (grupo.id) {
      // Grupo existente: atualizar grupo SEM modificadores aninhados (backend nao suporta)
      const payloadGrupo = {
        item_id: this.form.id!,
        descricao: grupo.descricao,
        minimoDeModificadores: grupo.minimoDeModificadores,
        maximoDeModificadores: grupo.maximoDeModificadores,
      };
      this.produtoVarianteModificadorService.updateGrupo(grupo.id, payloadGrupo as any).subscribe({
        next: (atualizado) => {
          Object.assign(grupo, { ...atualizado, modificadores: grupo.modificadores });
          this.salvarModificadoresDoGrupo(grupo, modificadoresValidos, () => {
            this.salvarGruposModificadoresSequencialEdicao(grupos, index + 1);
          });
        },
        error: () => {
          this.showError(this.translate.instant('produtos-edit.mensagens.erro_salvar_grupo_modificador'));
          this.salvarGruposModificadoresSequencialEdicao(grupos, index + 1);
        },
      });
    } else {
      // Grupo novo: criar primeiro, depois salvar modificadores com o id do grupo
      this.produtoVarianteModificadorService.createGrupo({
        ...grupo,
        item_id: this.form.id!,
        modificadores: [],
      }).subscribe({
        next: (criado) => {
          Object.assign(grupo, { ...criado, modificadores: grupo.modificadores });
          // Atribuir grupo id aos modificadores
          modificadoresValidos.forEach(mod => {
            mod.grupoDeModificadorDePrecoDeItem_id = criado.id!;
          });
          this.salvarModificadoresDoGrupo(grupo, modificadoresValidos, () => {
            this.salvarGruposModificadoresSequencialEdicao(grupos, index + 1);
          });
        },
        error: () => {
          this.showError(this.translate.instant('produtos-edit.mensagens.erro_salvar_grupo_modificador'));
          this.salvarGruposModificadoresSequencialEdicao(grupos, index + 1);
        },
      });
    }
  }

  /** Salva modificadores de um grupo (criar novos e atualizar existentes) */
  private salvarModificadoresDoGrupo(
    grupo: GrupoDeModificadorDePrecoDeItemDTO,
    modificadoresValidos: ModificadorDePrecoDeItemDTO[],
    callback: () => void
  ): void {
    if (modificadoresValidos.length === 0) {
      callback();
      return;
    }

    const requests = modificadoresValidos.map(mod => {
      const payloadMod = {
        item_id: this.form.id!,
        grupoDeModificadorDePrecoDeItem_id: grupo.id!,
        descricao: mod.descricao,
        precoDeVenda: mod.precoDeVenda,
      };
      if (mod.id) {
        return this.produtoVarianteModificadorService.updateModificador(mod.id, payloadMod as any);
      } else {
        return this.produtoVarianteModificadorService.createModificador(payloadMod as any);
      }
    });

    forkJoin(requests).subscribe({
      next: (resultados) => {
        // Atualizar modificadores locais com dados retornados
        let idx = 0;
        for (let i = 0; i < grupo.modificadores.length; i++) {
          if (grupo.modificadores[i].descricao?.trim()) {
            Object.assign(grupo.modificadores[i], resultados[idx]);
            idx++;
          }
        }
        callback();
      },
      error: () => {
        this.showError(this.translate.instant('produtos-edit.mensagens.erro_salvar_modificador'));
        callback();
      },
    });
  }

  adicionarModificador(grupoIndex: number): void {
    const grupo = this.gruposModificadores[grupoIndex];

    grupo.modificadores.push({
      item_id: this.form.id || 0,
      grupoDeModificadorDePrecoDeItem_id: grupo.id || 0,
      descricao: '',
      precoDeVenda: 0,
    });
    setTimeout(() => {
      const inputs = this.modificadorDescricaoInputs?.toArray();
      if (inputs?.length) {
        inputs[inputs.length - 1].nativeElement.focus();
      }
    });
  }

  removerModificador(grupoIndex: number, modIndex: number): void {
    const grupo = this.gruposModificadores[grupoIndex];
    const modificador = grupo.modificadores[modIndex];
    if (modificador.id) {
      this.produtoVarianteModificadorService.deleteModificador(modificador.id).subscribe({
        next: () => {
          grupo.modificadores.splice(modIndex, 1);
        },
        error: () => {
          this.showError(this.translate.instant('produtos-edit.mensagens.erro_remover_modificador'));
        },
      });
    } else {
      grupo.modificadores.splice(modIndex, 1);
    }
  }

  // =========================================================================
  // Submit (criar ou editar)
  // =========================================================================

  submitForm(): void {
    this.isFormSubmitted = true;

    const ehServico = this.form.tipoItem?.value === TipoItem.SERVICO;
    const unidadeValida = this.form.unidadeMedidaDeItem_id != null
      && this.form.unidadeMedidaDeItem_id !== 0;

    const camposFaltantes: string[] = [];
    if (!this.form.nome) camposFaltantes.push(this.translate.instant('produtos.labels.nome'));
    if (!this.form.grupo_id) camposFaltantes.push(this.translate.instant('produtos.labels.grupo'));
    if (!this.form.tipoItem) camposFaltantes.push(this.translate.instant('produtos.labels.tipo_de_item'));
    if (!this.form.tipoDeImposto) camposFaltantes.push(this.translate.instant('produtos.labels.tipo_de_imposto'));
    if (!unidadeValida) camposFaltantes.push(this.translate.instant('produtos.labels.unidade_de_medida'));
    // Preco de venda: invalido apenas se nao foi preenchido (null/undefined).
    // Zero e valor valido para qualquer tipo de item (cortesia, brinde, servico).
    if (this.form.precoDeVenda == null) camposFaltantes.push(this.translate.instant('produtos.labels.preco_de_venda'));
    if (!ehServico && this.form.custo == null) camposFaltantes.push(this.translate.instant('produtos.labels.custo'));
    // Saldo e estoque minimo: invalidos apenas se nao foram preenchidos
    // (null/undefined). Zero e valor valido — ex.: produto sem estoque inicial
    // ou que zerou.
    if (!ehServico && this.form.saldo == null) camposFaltantes.push(this.translate.instant('produtos.labels.saldo'));
    if (!ehServico && this.form.estoqueMinimo == null) camposFaltantes.push(this.translate.instant('produtos.labels.estoque_minimo'));

    if (camposFaltantes.length > 0) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('produtos.mensagens.campos_obrigatorios_titulo'),
        detail: this.translate.instant('produtos.mensagens.campos_obrigatorios_prefixo') + camposFaltantes.join(', '),
        life: 6000,
      });
      return;
    }

    // Validacao client-side de promocao/novidade (RN01-RN04). So exigida quando a
    // secao esta visivel (MiniShop ativo e nao item de sistema); caso contrario
    // os campos estao ocultos/zerados e passam direto.
    if (this.form.habilitarMiniShopping === true && !this.ehItemDeSistema
        && !this.validarPromocaoNovidadeOuAvisar()) {
      return;
    }

    if (this.form.id) {
      // Modo edicao
      this.salvarEdicao();
    } else {
      // Modo criacao
      this.salvarNovo();
    }
  }

  private salvarNovo(): void {
    this.form.status = true; // forca ativo por padrao

    const payload = this.mapFormToApiPayload(this.form);

    this.produtosService.post('/item', payload).subscribe({
      next: (produtoCriado: any) => {
        const produtoId = produtoCriado?.id;

        // Se tem variantes ou modificadores para salvar, salva apos criar o produto
        if (produtoId && (this.variantes.length > 0 || this.gruposModificadores.length > 0)) {
          this.salvarVariantesEModificadoresNovo(produtoId);
        } else {
          this.finalizarCriacao();
        }
      },
      error: (err) => {
        this.tratarErroSalvar(err, this.translate.instant('produtos.texts.erro_cadastrar_produto'));
      },
    });
  }

  private salvarVariantesEModificadoresNovo(produtoId: number): void {
    const requests: any[] = [];

    // Salvar variantes
    for (const variante of this.variantes) {
      if (variante.descricao?.trim()) {
        requests.push(
          this.produtoVarianteModificadorService.createVariante({
            ...variante,
            item_id: produtoId,
          })
        );
      }
    }

    const gruposParaSalvar = this.gruposModificadores.filter(g => g.descricao?.trim());

    if (requests.length === 0 && gruposParaSalvar.length === 0) {
      this.finalizarCriacao();
      return;
    }

    if (requests.length > 0) {
      forkJoin(requests).subscribe({
        next: () => {
          if (gruposParaSalvar.length > 0) {
            this.salvarGruposModificadoresSequencial(produtoId, gruposParaSalvar, 0);
          } else {
            this.finalizarCriacao();
          }
        },
        error: () => {
          this.messageService.add({
            severity: 'warn',
            summary: this.translate.instant('common.sucesso'),
            detail: this.translate.instant('produtos.mensagens.produto_cadastrado_variantes_erro'),
          });
          this.finalizarCriacao();
        },
      });
    } else if (gruposParaSalvar.length > 0) {
      this.salvarGruposModificadoresSequencial(produtoId, gruposParaSalvar, 0);
    }
  }

  private salvarGruposModificadoresSequencial(
    produtoId: number,
    grupos: GrupoDeModificadorDePrecoDeItemDTO[],
    index: number
  ): void {
    if (index >= grupos.length) {
      this.finalizarCriacao();
      return;
    }

    const grupo = grupos[index];
    const modificadoresLocais = grupo.modificadores.filter(m => m.descricao?.trim());

    this.produtoVarianteModificadorService.createGrupo({
      ...grupo,
      item_id: produtoId,
      modificadores: [],
    }).subscribe({
      next: (grupoCriado) => {
        if (grupoCriado.id && modificadoresLocais.length > 0) {
          const modRequests = modificadoresLocais.map(mod =>
            this.produtoVarianteModificadorService.createModificador({
              ...mod,
              item_id: produtoId,
              grupoDeModificadorDePrecoDeItem_id: grupoCriado.id!,
            })
          );

          forkJoin(modRequests).subscribe({
            next: () => this.salvarGruposModificadoresSequencial(produtoId, grupos, index + 1),
            error: () => this.salvarGruposModificadoresSequencial(produtoId, grupos, index + 1),
          });
        } else {
          this.salvarGruposModificadoresSequencial(produtoId, grupos, index + 1);
        }
      },
      error: () => {
        this.salvarGruposModificadoresSequencial(produtoId, grupos, index + 1);
      },
    });
  }

  private finalizarCriacao(): void {
    this.messageService.add({
      severity: 'success',
      summary: this.translate.instant('common.sucesso'),
      detail: this.translate.instant('produtos.mensagens.produto_cadastrado'),
    });
    this.form = this.createEmptyForm();
    this.precoDeVendaStr = this.formatarValorParaExibicao(this.form.precoDeVenda);
    this.custoStr = '';
    this.variantes = [];
    this.gruposModificadores = [];
    this.isFormSubmitted = false;
    this.imageBase64 = null;
    this.imagePreviewUrl = null;
    this.fileName = null;
    this.casasDecimaisEstoque = 0;
    this.resetarPromocaoNovidade();
    this.preSelectTipoItem();
    this.preencherUnidadeMedidaPadrao();
    this.activeTabIndex = 1;
    this.loadProdutos();
  }

  private salvarEdicao(): void {
    if (!this.form.id) return;

    const payload = this.mapFormToApiPayloadEdit(this.form);
    const produtoId = this.form.id;

    this.produtosService.put(`/item/${produtoId}`, payload).subscribe({
      next: () => {
        if (this.imagemAlterada) {
          const imagemPayload = { imagem: this.form.imagem || '' } as any;
          this.produtosService.put(`/item/atualizarImagem/${produtoId}`, imagemPayload).subscribe({
            next: () => this.finalizarEdicaoInline(),
            error: () => {
              this.messageService.add({
                severity: 'warn',
                summary: this.translate.instant('common.sucesso'),
                detail: this.translate.instant('produtos-edit.mensagens.erro_atualizar_imagem'),
              });
              this.finalizarEdicaoInline();
            },
          });
        } else {
          this.finalizarEdicaoInline();
        }
      },
      error: (err) => {
        this.tratarErroSalvar(err, this.translate.instant('produtos-edit.mensagens.erro_atualizar_produto'));
      },
    });
  }

  private finalizarEdicaoInline(): void {
    this.messageService.add({
      severity: 'success',
      summary: this.translate.instant('common.sucesso'),
      detail: this.translate.instant('produtos-edit.mensagens.produto_atualizado'),
    });
    this.form = this.createEmptyForm();
    this.precoDeVendaStr = this.formatarValorParaExibicao(this.form.precoDeVenda);
    this.custoStr = '';
    this.variantes = [];
    this.gruposModificadores = [];
    this.isFormSubmitted = false;
    this.imageBase64 = null;
    this.imagePreviewUrl = null;
    this.fileName = null;
    this.imagemAlterada = false;
    this.resetarPromocaoNovidade();
    this.preSelectTipoItem();
    this.activeTabIndex = 1;
    this.loadProdutos();
  }

  // =========================================================================
  // Image Upload (formulario)
  // =========================================================================

  private readonly formatosImagemPermitidos = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];

  async onSelectImage(event: any) {
    const file: File | undefined = event.files?.[0];
    if (!file) return;

    if (!this.formatosImagemPermitidos.includes(file.type)) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.arquivo_invalido'),
        detail: this.translate.instant('produtos.mensagens.formato_imagem_invalido'),
      });
      return;
    }

    this.fileName = file.name;
    const imagem = await ImagemUtils.comprimirParaBase64(file);
    this.imagePreviewUrl = imagem.dataUrl;
    this.imageBase64 = imagem.base64;
    this.form.imagem = this.imageBase64;
    this.imagemAlterada = true;
  }

  onClearImage() {
    this.fileName = null;
    this.imageBase64 = null;
    this.imagePreviewUrl = null;
    this.form.imagem = '';
    this.imagemAlterada = true;
  }

  // Lightbox (expansao da imagem no formulario de edicao)
  abrirImagemExpandida(): void {
    if (this.imagePreviewUrl) {
      this.imagemExpandida = true;
    }
  }

  fecharImagemExpandida(): void {
    this.imagemExpandida = false;
  }

  // =========================================================================
  // Formatacao de preco
  // =========================================================================

  formatarCampoPreco(event: any, campo: 'precoDeVenda' | 'custo'): void {
    const input = event.target as HTMLInputElement;
    const raw = input.value || '';
    const onlyDigits = raw.replace(/\D+/g, '');
    const casasDecimais = this.casasDecimaisDaMoeda;
    const strCampo = campo === 'precoDeVenda' ? 'precoDeVendaStr' : 'custoStr';

    // Quando o usuario apaga tudo, o campo volta automaticamente para 0
    // (formatado conforme a moeda: "0" para PYG, "0,00" para BRL/USD).
    if (!onlyDigits) {
      const formattedZero = this.formatarValorParaExibicao(0);
      (this.form as any)[campo] = 0;
      this[strCampo] = formattedZero;
      input.value = formattedZero;
      return;
    }

    const { valor, formatted } = this.calcularValorFormatado(onlyDigits, casasDecimais);
    (this.form as any)[campo] = valor;
    input.value = formatted;
    this[strCampo] = formatted;
  }

  formatarCampoPrecoVariante(event: any, variante: VarianteDePrecoDeItemDTO): void {
    const input = event.target as HTMLInputElement;
    const raw = input.value || '';
    const onlyDigits = raw.replace(/\D+/g, '');

    if (!onlyDigits) {
      variante.precoDeVenda = 0;
      input.value = '';
      return;
    }

    const { valor, formatted } = this.calcularValorFormatado(onlyDigits, this.casasDecimaisDaMoeda);
    variante.precoDeVenda = valor ?? 0;
    input.value = formatted;
  }

  formatarCampoPrecoModificador(event: any, mod: ModificadorDePrecoDeItemDTO): void {
    const input = event.target as HTMLInputElement;
    const raw = input.value || '';
    const onlyDigits = raw.replace(/\D+/g, '');

    if (!onlyDigits) {
      mod.precoDeVenda = 0;
      input.value = '';
      return;
    }

    const { valor, formatted } = this.calcularValorFormatado(onlyDigits, this.casasDecimaisDaMoeda);
    mod.precoDeVenda = valor ?? 0;
    input.value = formatted;
  }

  /** Calcula valor numerico e string formatada a partir de digitos puros.
   *  Trata 0 como valor valido (zero NAO e equivalente a "vazio").
   *
   *  Usa `localeMoeda` (carregado da empresa) tanto para 0 quanto para 2 casas
   *  decimais — garante separador decimal/milhar consistente com a moeda da
   *  empresa (ex: pt-BR usa virgula, en-US usa ponto). */
  private calcularValorFormatado(onlyDigits: string, casasDecimais: number): { valor: number | null; formatted: string } {
    if (casasDecimais === 0) {
      // Guarani (PYG): sem centavos, apenas separador de milhar
      const intVal = parseInt(onlyDigits, 10);
      if (isNaN(intVal)) {
        return { valor: null, formatted: '' };
      }
      const formatted = intVal.toLocaleString(this.localeMoeda, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
      return { valor: intVal, formatted };
    } else {
      // Real / Dolar / Peso: 2 casas decimais
      const cents = parseInt(onlyDigits, 10);
      if (isNaN(cents)) {
        return { valor: null, formatted: '' };
      }
      const val = cents / 100;
      const formatted = val.toLocaleString(this.localeMoeda, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      return { valor: val, formatted };
    }
  }

  /** Formata valor numerico para exibicao no input (ao carregar para edicao).
   *  Zero e valor valido — exibe "0" formatado, nao string vazia.
   *  Usa `localeMoeda` da empresa para garantir separador decimal/milhar
   *  consistente. */
  formatarValorParaExibicao(valor: number | null | undefined): string {
    if (valor == null || isNaN(valor) || valor < 0) return '';
    const casasDecimais = this.casasDecimaisDaMoeda;

    if (casasDecimais === 0) {
      const intVal = Math.round(valor);
      return intVal.toLocaleString(this.localeMoeda, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    } else {
      return valor.toLocaleString(this.localeMoeda, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
  }

  /** Quando o grupo muda, limpa a selecao de subgrupo */
  onGrupoChange(grupoId: any): void {
    if (this.isLoadingProduto) return;
    this.form.subGrupo_id = undefined;
  }

  // =========================================================================
  // Atalhos de teclado
  // =========================================================================

  @HostListener('document:keydown', ['$event'])
  onGlobalKeydown(ev: KeyboardEvent): void {
    // F2: focar no input de busca (apenas na aba lista)
    if (ev.key === 'F2') {
      ev.preventDefault();
      if (!this.isDialogAberto() && this.activeTabIndex === 1) {
        setTimeout(() => {
          this.searchInputRef?.nativeElement?.focus();
        }, 100);
      }
      return;
    }

    // Ctrl+Enter
    if (ev.ctrlKey && ev.key === 'Enter') {
      if (this.isDialogAberto()) return;

      ev.preventDefault();

      if (this.activeTabIndex === 0) {
        // Na aba cadastro: salvar produto
        this.submitForm();
      } else {
        // Na aba lista: ir para novo produto
        this.novoProduto();
      }
      return;
    }

    // Escape
    if (ev.key === 'Escape') {
      ev.preventDefault();
      if (this.mostrarDialogRemoverGrupo) {
        this.cancelarRemocaoGrupo();
      } else if (this.imagemExpandida) {
        this.fecharImagemExpandida();
      } else if (this.activeTabIndex === 0) {
        // Se na aba cadastro, voltar para lista
        this.activeTabIndex = 1;
      }
      return;
    }

    // Enter: confirmar remocao do grupo quando dialog esta aberto
    if (ev.key === 'Enter' && !ev.ctrlKey && this.mostrarDialogRemoverGrupo) {
      ev.preventDefault();
      this.confirmarRemocaoGrupoFinal();
      return;
    }
  }

  private isDialogAberto(): boolean {
    return this.displayDialogImage || this.displayDialogSaldo || this.deleteDialogVisible || this.displayDialogEtiqueta || this.mostrarDialogRemoverGrupo || !!this.produtoSelecionadoParaImagens;
  }

  // =========================================================================
  // Track By (evita recriacao do DOM pelo Angular)
  // =========================================================================

  trackByVariante(index: number, variante: VarianteDePrecoDeItemDTO): any {
    return variante.id ?? `new-${index}`;
  }

  trackByGrupo(index: number, grupo: GrupoDeModificadorDePrecoDeItemDTO): any {
    return grupo.id ?? `new-${index}`;
  }

  trackByModificador(index: number, mod: ModificadorDePrecoDeItemDTO): any {
    return mod.id ?? `new-${index}`;
  }

  // =========================================================================
  // Utilitarios - Mapeamento de payload
  // =========================================================================

  /** Payload para atualizar saldo da lista (usado no dialog de saldo) */
  private mapProdutoListaToPayload(produto: any): any {
    return {
      id: this.getValueOrNull(produto.id),
      codigoItem: this.getValueOrNull(produto.codigoItem),
      codigoFabricante: this.getValueOrNull(produto.codigoFabricante),
      nome: this.getValueOrNull(produto.nome),
      barras: this.getValueOrNull(produto.barras),

      grupo_id: this.getValueOrNull(produto.grupo?.id),
      subGrupo_id: this.getValueOrNull(produto.subGrupo?.id),
      marca_id: this.getValueOrNull(produto.marca_id),
      fornecedorPrincipal_id: this.getValueOrNull(produto.fornecedorPrincipal?.id),
      unidadeMedidaDeItem_id: this.getValueOrNull(produto.unidadeMedidaDeItem?.id),

      tipoDeImposto: this.getStringValue(produto.tipoDeImposto?.enum),
      tipoDeDesconto: this.getStringValue(produto.tipoDeDesconto?.enum),
      tipoItem: this.getStringValue(produto.tipoItem?.enum),

      status: this.toBoolean(produto.status),
      permiteImprimir: this.toBoolean(produto.permiteImprimir),
      permiteImprimirPedido: this.toBoolean(produto.permiteImprimirPedido),
      imagem: produto.imagem,

      precoDeVenda: this.toNumber(produto.precoDeVenda),
      custo: this.toNumber(produto.custo),
      desconto: this.toNumber(produto.desconto),
      estoqueMinimo: this.toNumber(produto.estoqueMinimo),
      saldo: this.toNumber(produto.saldo),
      impressoraPadraoId: this.getValueOrNull(produto.impressoraPadraoId),
      impressoraPadraoPedidoId: this.getValueOrNull(produto.impressoraPadraoPedidoId),
    };
  }

  /** Payload para criar novo produto (formulario cadastro) */
  private mapFormToApiPayload(form: ProdutosDTO): any {
    const ehServico = form.tipoItem?.value === TipoItem.SERVICO;
    const miniShopAtivo = this.toBoolean(form.habilitarMiniShopping);
    return {
      ...form,
      grupo_id: this.getValueOrNull(form.grupo_id),
      subGrupo_id: this.getValueOrNull(form.subGrupo_id),
      marca_id: this.getValueOrNull(form.marca_id),
      fornecedorPrincipal_id: ehServico ? null : this.getValueOrNull(form.fornecedorPrincipal_id),
      unidadeMedidaDeItem_id: this.getValueOrNull(form.unidadeMedidaDeItem_id),

      tipoDeImposto: this.getStringValue(form.tipoDeImposto),
      tipoDeDesconto: this.getStringValue(form.tipoDeDesconto),
      tipoItem: this.getStringValue(form.tipoItem),

      status: this.toBoolean(form.status),
      venderNoMiniShop: this.toBoolean(form.habilitarMiniShopping),
      permiteImprimir: this.toBoolean(form.permiteImprimir),
      permiteImprimirPedido: this.toBoolean(form.permiteImprimirPedido),

      precoDeVenda: this.toNumber(form.precoDeVenda),
      custo: ehServico ? 0 : this.toNumber(form.custo),
      desconto: this.toNumber(form.desconto),
      estoqueMinimo: ehServico ? 0 : this.toNumber(form.estoqueMinimo),
      saldo: ehServico ? 0 : this.toNumber(form.saldo),
      impressoraPadraoId: this.getValueOrNull(form.impressoraPadraoId),
      impressoraPadraoPedidoId: this.getValueOrNull(form.impressoraPadraoPedidoId),

      // Promocao/Novidade: so e enviada quando o MiniShop esta ativo (secao visivel).
      // Com MiniShop desligado, zera tudo para nao gravar promocao/novidade "fantasma".
      // Data serializada como ISO local apenas quando a flag esta ligada (RN05).
      promocao: miniShopAtivo && this.promocao,
      promocaoAteEm: miniShopAtivo && this.promocao && this.promocaoAteEmData ? toLocalISOString(this.promocaoAteEmData) : null,
      novidade: miniShopAtivo && this.novidade,
      novidadeAteEm: miniShopAtivo && this.novidade && this.novidadeAteEmData ? toLocalISOString(this.novidadeAteEmData) : null,
      // Campos exibidos no shop (atributos gerais do item).
      indicado: this.indicado?.trim() || null,
      genero: this.genero ?? null,
    };
  }

  /** Payload para editar produto (formulario edicao) */
  private mapFormToApiPayloadEdit(form: ProdutosDTO): any {
    const ehServico = form.tipoItem?.value === TipoItem.SERVICO;
    const miniShopAtivo = this.toBoolean(form.habilitarMiniShopping);
    return {
      id: this.getValueOrNull(form.id),
      codigoItem: this.getValueOrNull(form.codigoItem),
      codigoFabricante: this.getValueOrNull(form.codigoFabricante),
      nome: this.getValueOrNull(form.nome),
      barras: this.getValueOrNull(form.barras),

      grupo_id: this.getValueOrNull(form.grupo_id),
      subGrupo_id: this.getValueOrNull(form.subGrupo_id),
      marca_id: this.getValueOrNull(form.marca_id),
      fornecedorPrincipal_id: ehServico ? null : this.getValueOrNull(form.fornecedorPrincipal_id),
      unidadeMedidaDeItem_id: this.getValueOrNull(form.unidadeMedidaDeItem_id),

      tipoDeImposto: this.getStringValue(form.tipoDeImposto),
      tipoDeDesconto: this.getStringValue(form.tipoDeDesconto),
      tipoItem: this.getStringValue(form.tipoItem?.value),

      status: this.toBoolean(form.status),
      venderNoMiniShop: this.toBoolean(form.habilitarMiniShopping),
      permiteImprimir: this.toBoolean(form.permiteImprimir),
      permiteImprimirPedido: this.toBoolean(form.permiteImprimirPedido),
      descricao: form.descricao || null,

      precoDeVenda: this.toNumber(form.precoDeVenda),
      custo: ehServico ? 0 : this.toNumber(form.custo),
      desconto: this.toNumber(form.desconto),
      estoqueMinimo: ehServico ? 0 : this.toNumber(form.estoqueMinimo),
      // Na edicao, enviar sempre o saldo original do backend (igual ao mobile).
      // Se for SERVICO, forcar 0 independente do saldo original.
      saldo: ehServico ? 0 : this.saldoOriginal,
      impressoraPadraoId: this.getValueOrNull(form.impressoraPadraoId),
      impressoraPadraoPedidoId: this.getValueOrNull(form.impressoraPadraoPedidoId),

      // Promocao/Novidade: so e enviada quando o MiniShop esta ativo (secao visivel).
      // Com MiniShop desligado, zera tudo para nao gravar promocao/novidade "fantasma".
      // Data serializada como ISO local apenas quando a flag esta ligada (RN05).
      // Item de sistema mantem false/null (RN08), pois a secao fica oculta.
      promocao: miniShopAtivo && this.promocao,
      promocaoAteEm: miniShopAtivo && this.promocao && this.promocaoAteEmData ? toLocalISOString(this.promocaoAteEmData) : null,
      novidade: miniShopAtivo && this.novidade,
      novidadeAteEm: miniShopAtivo && this.novidade && this.novidadeAteEmData ? toLocalISOString(this.novidadeAteEmData) : null,
      // Campos exibidos no shop (atributos gerais do item).
      indicado: this.indicado?.trim() || null,
      genero: this.genero ?? null,
    };
  }

  // =========================================================================
  // Utilitarios - Helpers
  // =========================================================================

  private createEmptyForm(): ProdutosDTO {
    return {
      nome: '',
      barras: '',
      codigoFabricante: '',
      unidadeMedidaDeItem_id: 0,
      precoDeVenda: 0,
      tipoDeImposto: 'IVA_10',
      custo: null as any,
      grupo_id: undefined,
      subGrupo_id: undefined,
      marca_id: undefined,
      // Saldo e estoque minimo iniciam em 0 — zero e valor valido (ex.:
      // produto recem-cadastrado sem entrada em estoque ainda).
      estoqueMinimo: 0,
      tipoDeDesconto: 'SEM_DESCONTO',
      desconto: 0,
      fornecedorPrincipal_id: undefined,
      status: true,
      saldo: 0,
      imagem: undefined,
      descricao: '',
      tipoItem: undefined as any,
      habilitarMiniShopping: false,
      impressoraPadraoId: undefined,
      permiteImprimir: false,
      // Item novo ja nasce com a impressora de pedido da empresa, como o app faz. O booleano,
      // porem, nasce `false` sem herdar da empresa — assimetria proposital do app: a empresa
      // imprimir pedidos automaticamente nao significa que todo item deve ir para a producao.
      impressoraPadraoPedidoId: this.impressoraPadraoPedidoEmpresaConfig ?? undefined,
      permiteImprimirPedido: false,
    };
  }

  private getValueOrNull(obj: any): number | null {
    return obj && typeof obj === 'object' && 'value' in obj ? obj.value : obj ?? null;
  }

  // =========================================================================
  // Promocao e Novidade (inline)
  // =========================================================================

  /** Zera o estado de promocao/novidade (usado ao resetar o formulario inline). */
  private resetarPromocaoNovidade(): void {
    this.promocao = false;
    this.promocaoAteEmData = null;
    this.novidade = false;
    this.novidadeAteEmData = null;
    this.indicado = '';
    this.genero = null;
    this.ehItemDeSistema = false;
  }

  /**
   * Ao desligar o MiniShop, a secao de promocao/novidade fica oculta;
   * zera as flags e datas para nao gravar promocao/novidade "fantasma".
   */
  onMiniShopChange(): void {
    if (this.form.habilitarMiniShopping !== true) {
      this.promocao = false;
      this.promocaoAteEmData = null;
      this.novidade = false;
      this.novidadeAteEmData = null;
    }
  }

  /** Ao desligar o switch de promocao, zera a data (evita "valor fantasma" — RN05). */
  onPromocaoChange(): void {
    if (!this.promocao) {
      this.promocaoAteEmData = null;
    }
  }

  /** Ao desligar o switch de novidade, zera a data (evita "valor fantasma" — RN05). */
  onNovidadeChange(): void {
    if (!this.novidade) {
      this.novidadeAteEmData = null;
    }
  }

  /**
   * Valida promocao/novidade e, se houver erros, exibe-os via MessageService.
   * @returns `true` quando esta valido (pode prosseguir com o submit).
   */
  private validarPromocaoNovidadeOuAvisar(): boolean {
    const validacao = validarPromocaoNovidade(
      this.promocao,
      this.promocaoAteEmData,
      this.novidade,
      this.novidadeAteEmData,
    );
    if (!validacao.ok) {
      validacao.errosI18n.forEach((chave) => {
        this.messageService.add({
          severity: 'warn',
          summary: this.translate.instant('produtos.mensagens.campos_obrigatorios_titulo'),
          detail: this.translate.instant(chave),
          life: 6000,
        });
      });
      return false;
    }
    return true;
  }

  /** Exibe erro de salvamento — detalha array `erros` 400 (RN01-RN04) ou cai no generico. */
  private tratarErroSalvar(err: any, mensagemPadrao: string): void {
    const erros400 = extrairErros400(err, mensagemPadrao);
    if (err?.status === 400 && erros400.length > 0) {
      erros400.forEach((detail) => this.showError(detail));
      return;
    }
    this.showError(err?.error?.message || mensagemPadrao);
  }

  private getStringValue(obj: any): string | null {
    if (!obj || (typeof obj === 'string' && obj.trim() === '')) {
      return null;
    }
    return obj && typeof obj === 'object' && 'value' in obj ? obj.value : obj;
  }

  private toNumber(value: any): number {
    return value !== undefined && value !== null ? Number(value) : 0;
  }

  private toBoolean(value: any): boolean {
    return value === true || value === 'true';
  }

  /** Handler chamado quando a unidade de medida muda no dropdown do formulario inline */
  onUnidadeMedidaChange(): void {
    const id = this.form.unidadeMedidaDeItem_id as number;
    this.atualizarCasasDecimaisEstoque(id ?? 0);
  }

  /** Atualiza casas decimais de estoque com base na unidade selecionada e reformata valores */
  private atualizarCasasDecimaisEstoque(unidadeId: number): void {
    const novasCasas = this.unidadeMedidaCasasDecimaisMap.get(unidadeId) ?? 0;
    const casasAnteriores = this.casasDecimaisEstoque;
    this.casasDecimaisEstoque = novasCasas;

    // Reformatar valores existentes se casas decimais diminuiu
    if (novasCasas < casasAnteriores) {
      if (this.form.saldo != null) {
        this.form.saldo = this.arredondarParaCasas(this.form.saldo, novasCasas);
      }
      if (this.form.estoqueMinimo != null) {
        this.form.estoqueMinimo = this.arredondarParaCasas(this.form.estoqueMinimo, novasCasas);
      }
    }
  }

  /** Arredonda um valor para o numero de casas decimais especificado */
  private arredondarParaCasas(valor: number, casas: number): number {
    const fator = Math.pow(10, casas);
    return Math.round(valor * fator) / fator;
  }

  /** Retorna as casas decimais da unidade de medida de um produto (para exibicao na tabela) */
  getCasasDecimaisProduto(produto: any): number {
    const unidadeId = produto.unidadeMedidaDeItem?.id ?? produto.unidadeMedidaDeItem_id ?? 0;
    return this.unidadeMedidaCasasDecimaisMap.get(unidadeId) ?? 0;
  }

  /** Formata o saldo do produto com as casas decimais corretas da unidade */
  formatarSaldoProduto(produto: any): string {
    const casas = this.getCasasDecimaisProduto(produto);
    const saldo = produto.saldo ?? 0;
    return casas > 0 ? saldo.toFixed(casas) : String(Math.round(saldo));
  }

  private showError(_detail: string): void {
    // Toast de erro exibido pelo HttpErrorInterceptor global
  }

}
